<template>
    <div>
        <v-navigation-drawer :value="task_details_dialog" v-click-outside="{
            handler: closeTaskDetailsDialog,
            closeConditional: onCloseConditional,
            include: includeFunction,
        }" clipped app right temporary :width="navigation.width" ref="drawer" class="task-details-drawer" data-tour="task-details-drawer">
            <div v-if="get_selected_task" class="task-details-container">
                <!-- Header -->
                <div class="task-header">
                    <div class="task-header-top">
                        <!-- Task Title -->
                        <div class="task-title-section">
                            <div class="field-label field-label-required">Task Name</div>
                            <v-textarea v-model="get_selected_task.name" placeholder="Task Name" solo flat dense
                                hide-details class="task-title-field" @blur="onTaskNameBlur"
                                data-tour="task-title-field" rows="1" auto-grow />
                        </div>

                        <div class="task-header-actions">
                            <v-tooltip bottom v-if="canSendAssigneeReminder">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" fab x-small depressed outlined
                                        color="orange darken-2" :loading="sendingAssigneeReminder"
                                        @click="sendAssigneeReminder()" class="icon-action-btn mr-2">
                                        <v-icon small>mdi-bell-ring-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>Send follow-up reminder to assignees</span>
                            </v-tooltip>
                            <v-btn @click="closeTaskDetailsDialog()" fab x-small depressed outlined
                                color="grey darken-1" class="icon-action-btn">
                                <v-icon small>mdi-close</v-icon>
                            </v-btn>
                        </div>
                    </div>
                </div>

                <v-divider />

                <!-- Main Content -->
                <div class="task-content">
                    <!-- Task Properties Grid -->
                    <div class="task-properties">
                        <div class="property-grid-compact" data-tour="property-grid-compact">
                            <!-- Company / Assigned To Row -->
                            <div class="property-row">
                                <div class="field-block">
                                    <div class="field-label field-label-required">Company</div>
                                    <v-select v-model="get_selected_task.company_id"
                                        :items="[{ name: 'No Company', id: null }, ...get_companies]"
                                        item-text="name" item-value="id" dense solo flat hide-details
                                        prepend-inner-icon="mdi-domain" placeholder="No Company"
                                        class="field-outlined" @change="updateTask()" />
                                </div>

                                <div class="field-block">
                                    <div class="field-label">Assigned To</div>
                                    <v-select :value="getAssigneeIds()" @input="updateTaskAssignees" :items="users"
                                        item-text="name" item-value="id" multiple dense solo flat hide-details
                                        class="field-outlined assignee-select" placeholder="Select assignees">
                                        <template v-slot:item="{ item, on, attrs }">
                                            <v-list-item v-bind="attrs" v-on="on">
                                                <v-list-item-avatar size="24">
                                                    <v-avatar size="24" :color="getAvatarColor(item.name)">
                                                        <span class="avatar-text">{{ getInitials(item.name) }}</span>
                                                    </v-avatar>
                                                </v-list-item-avatar>
                                                <v-list-item-content>
                                                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </template>

                                        <template v-slot:selection="{ item }">
                                            <v-chip x-small :color="getAvatarColor(item.name)" dark
                                                class="assignee-chip">
                                                <v-avatar left size="16" :color="getAvatarColor(item.name)">
                                                    <span class="chip-avatar-text">{{ getInitials(item.name) }}</span>
                                                </v-avatar>
                                                {{ item.name }}
                                            </v-chip>
                                        </template>
                                    </v-select>
                                </div>
                            </div>

                            <!-- Priority, Status, Due Date, Tags Row -->
                            <div class="property-row property-row-quad">
                                <div class="field-block">
                                    <div class="field-label">Priority</div>
                                    <v-select v-model="get_selected_task.priority_id" :items="get_priorities"
                                        item-text="name" item-value="id" dense solo flat hide-details
                                        class="field-outlined" @change="updateTask()">
                                        <template #selection="{ item }">
                                            <span class="field-inline-value">
                                                <v-icon small :color="getPriorityChipColor(item.id)"
                                                    class="mr-1">mdi-flag</v-icon>
                                                {{ item.name }}
                                            </span>
                                        </template>
                                    </v-select>
                                </div>

                                <div class="field-block">
                                    <div class="field-label">Status</div>
                                    <v-select :value="get_selected_task.status_id" :items="get_statuses"
                                        item-text="name" item-value="id" dense solo flat hide-details
                                        class="field-outlined"
                                        @change="updateTaskStatus($event)"
                                        :key="`status-${get_selected_task.id}-${statusSelectKey}`">
                                        <template #selection="{ item }">
                                            <v-chip x-small :color="getStatusChipColor(item.id)"
                                                :text-color="getStatusTextColor(item.id)" class="status-chip">
                                                <v-icon left x-small>mdi-checkbox-marked-circle</v-icon>
                                                {{ item.name }}
                                            </v-chip>
                                        </template>
                                    </v-select>
                                </div>

                                <div class="field-block">
                                    <div class="field-label">Due Date</div>
                                    <v-menu v-model="due_date_menu" :close-on-content-click="false"
                                        transition="scale-transition" offset-y max-width="290px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <div v-bind="attrs" v-on="on" class="field-outlined field-button">
                                                <v-icon small class="mr-1" color="grey darken-1">mdi-calendar</v-icon>
                                                <span class="field-inline-value">{{ formatDueDate(get_selected_task.due_date) }}</span>
                                                <v-spacer />
                                                <v-icon small color="grey">mdi-chevron-down</v-icon>
                                            </div>
                                        </template>
                                        <v-date-picker v-model="get_selected_task.due_date"
                                            @input="due_date_menu = false; updateTask();" no-title scrollable />
                                    </v-menu>
                                </div>

                                <div class="field-block">
                                    <div class="field-label">Tags</div>
                                    <v-combobox v-model="get_selected_task.tags" :items="availableTags" dense
                                        solo flat hide-details class="field-outlined" @change="updateTask()"
                                        prepend-inner-icon="mdi-tag" multiple chips small-chips deletable-chips
                                        placeholder="Add tags...">
                                        <template #selection="{ item }">
                                            <v-chip x-small color="primary" dark class="tag-chip">
                                                {{ item }}
                                            </v-chip>
                                        </template>
                                        <template #item="{ item }">
                                            <v-list-item-content>
                                                <v-list-item-title>{{ item }}</v-list-item-title>
                                            </v-list-item-content>
                                        </template>
                                        <template #no-data>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-title>
                                                        Press <kbd>enter</kbd> to create a new tag
                                                    </v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </template>
                                    </v-combobox>
                                </div>
                            </div>
                        </div>
                    </div>

                    <v-divider class="section-divider" />

                    <!-- Time Allocation Section -->
                    <div class="task-properties">
                        <div class="section-header section-header-tight">
                            <h3 class="section-title">
                                <v-icon left>mdi-clock-outline</v-icon>
                                Time Allocation
                            </h3>
                        </div>

                        <div class="property-row">
                            <div class="field-block">
                                <div class="field-label">Start</div>
                                <v-text-field v-model="get_selected_task.start_time" type="datetime-local" dense
                                    solo flat hide-details @input="updateTaskTiming" class="field-outlined"
                                    clearable @click:clear="updateTaskTiming">
                                </v-text-field>
                            </div>

                            <div class="field-block">
                                <div class="field-label">End</div>
                                <v-text-field v-model="get_selected_task.end_time" type="datetime-local" dense
                                    solo flat hide-details @input="updateTaskTiming" class="field-outlined"
                                    clearable @click:clear="updateTaskTiming">
                                </v-text-field>
                            </div>

                            <div class="field-block">
                                <div class="field-label">Allocated</div>
                                <div class="field-outlined field-static">
                                    <v-icon small class="mr-1" color="grey darken-1">mdi-timer-outline</v-icon>
                                    <span class="field-inline-value">{{ formatAllocatedTime() }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <v-divider class="section-divider" />

                    <!-- Recurrence Pattern Section (shown only when status is recurring - status_id = 7) -->
                    <div class="task-properties" v-if="get_selected_task.status_id === 7">
                        <div class="section-header section-header-tight">
                            <h3 class="section-title">
                                <v-icon left>mdi-repeat</v-icon>
                                Recurrence
                            </h3>
                        </div>

                        <div class="recurrence-row">
                            <span class="recurrence-label">Repeats every</span>
                            <v-text-field
                                v-model.number="get_selected_task.recurrence_interval"
                                type="number"
                                min="1"
                                max="52"
                                solo
                                flat
                                dense
                                hide-details
                                class="field-outlined recurrence-interval-field"
                                @change="updateTask()"
                            ></v-text-field>
                            <span class="field-static recurrence-unit-field">week(s)</span>
                        </div>

                        <div class="recurrence-row recurrence-row-days">
                            <span class="recurrence-label">On these days:</span>
                            <v-btn-toggle dense class="recurrence-toggle-group">
                                <v-btn x-small outlined color="primary" @click="selectWeekdays">Daily</v-btn>
                                <v-btn x-small outlined color="secondary" @click="clearAllDays">Clear</v-btn>
                            </v-btn-toggle>
                        </div>
                        <div class="recurrence-days">
                            <v-chip
                                v-for="day in daysOfWeek"
                                :key="day.value"
                                :color="(get_selected_task.recurrence_days || []).includes(day.value) ? 'primary' : 'default'"
                                :text-color="(get_selected_task.recurrence_days || []).includes(day.value) ? 'white' : 'black'"
                                small
                                clickable
                                @click="toggleRecurrenceDay(day.value)"
                            >
                                {{ day.label }}
                            </v-chip>
                        </div>

                        <div class="property-row">
                            <div class="field-block">
                                <div class="field-label">Series Start</div>
                                <v-menu v-model="seriesStartDateMenu" :close-on-content-click="false" offset-y max-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on" class="field-outlined field-button">
                                            <v-icon small class="mr-1" color="grey darken-1">mdi-calendar-start</v-icon>
                                            <span class="field-inline-value">{{ formatDateValue(get_selected_task.series_start_date) || 'Select date' }}</span>
                                            <v-spacer />
                                            <v-icon small color="grey">mdi-chevron-down</v-icon>
                                        </div>
                                    </template>
                                    <v-date-picker
                                        :value="formatDateValue(get_selected_task.series_start_date)"
                                        @input="updateSeriesStartDate"
                                    ></v-date-picker>
                                </v-menu>
                            </div>
                            <div class="field-block">
                                <div class="field-label">Series End</div>
                                <v-menu v-model="seriesEndDateMenu" :close-on-content-click="false" offset-y max-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on" class="field-outlined field-button">
                                            <v-icon small class="mr-1" color="grey darken-1">mdi-calendar-end</v-icon>
                                            <span class="field-inline-value">{{ formatDateValue(get_selected_task.series_end_date) || 'Select date (optional)' }}</span>
                                            <v-spacer />
                                            <v-btn v-if="get_selected_task.series_end_date" icon x-small
                                                @click.stop="clearSeriesEndDate">
                                                <v-icon x-small>mdi-close</v-icon>
                                            </v-btn>
                                            <v-icon v-else small color="grey">mdi-chevron-down</v-icon>
                                        </div>
                                    </template>
                                    <v-date-picker
                                        :value="formatDateValue(get_selected_task.series_end_date)"
                                        :min="formatDateValue(get_selected_task.series_start_date)"
                                        @input="updateSeriesEndDate"
                                    ></v-date-picker>
                                </v-menu>
                            </div>
                        </div>

                        <div class="recurrence-banner">
                            <v-icon small color="success darken-1" class="mr-2">mdi-repeat</v-icon>
                            This task repeats: {{ formatRecurrencePattern(get_selected_task) }}
                        </div>
                    </div>

                    <v-divider class="section-divider" />

                    <!-- Comments & Activity Section - Moved up -->
                    <div class="activity-section" data-tour="activity-section">
                        <div class="section-header">
                            <h3 class="section-title">
                                <v-icon left>mdi-timeline</v-icon>
                                Activity
                            </h3>
                            <v-menu bottom left>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small v-bind="attrs" v-on="on">
                                        <v-icon small>mdi-sort</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense>
                                    <v-list-item @click="activitySortOrder = 'newest'"
                                        :class="{ 'v-list-item--active': activitySortOrder === 'newest' }">
                                        <v-list-item-icon>
                                            <v-icon small>mdi-sort-descending</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>Newest</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="activitySortOrder = 'oldest'"
                                        :class="{ 'v-list-item--active': activitySortOrder === 'oldest' }">
                                        <v-list-item-icon>
                                            <v-icon small>mdi-sort-ascending</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>Oldest</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>

                        <v-tabs v-model="activeActivityTab" grow>
                            <v-tab>
                                <v-icon left small>mdi-comment-multiple</v-icon>
                                Comments
                                <v-chip small outlined class="ml-2">
                                    {{ (get_selected_task.task_comments || []).length }}
                                </v-chip>
                            </v-tab>
                            <v-tab>
                                <v-icon left small>mdi-timeline</v-icon>
                                All activity
                                <v-chip small outlined class="ml-2">
                                    {{ allActivityItems.length }}
                                </v-chip>
                            </v-tab>

                            <v-tab-item>
                                <!-- Comments Tab Content -->
                                <div class="tab-content">
                                    <div v-if="!get_selected_task.task_comments || get_selected_task.task_comments.length === 0"
                                        class="no-comments">
                                        <v-icon color="grey">mdi-comment-multiple</v-icon>
                                        <span>No comments yet</span>
                                    </div>

                                    <div v-else class="comments-list">
                                        <div v-for="task_comment in organizedComments"
                                            :key="'comment-' + task_comment.id" class="comment-item" :class="{
                                                'reply-comment': task_comment.parent_id,
                                                'highlighted-comment': highlightedComment === task_comment.id
                                            }">
                                            <div class="comment-header">
                                                <v-avatar :color="getAvatarColor(task_comment.user.name)" size="32"
                                                    class="comment-avatar">
                                                    <span class="avatar-text">{{ getInitials(task_comment.user.name)
                                                        }}</span>
                                                </v-avatar>
                                                <div class="comment-meta">
                                                    <div class="comment-author">{{ task_comment.user.name }}</div>
                                                    <div class="comment-time">
                                                        {{ $date(task_comment.created_at).fromNow() }} •
                                                        {{ $date(task_comment.created_at).format(`MMM DD, YYYY ${$tz.time}`)
                                                        }}
                                                    </div>
                                                </div>
                                                <v-spacer />
                                                <v-menu v-if="get_user.id == task_comment.user_id" bottom left>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn icon small v-bind="attrs" v-on="on">
                                                            <v-icon small>mdi-dots-horizontal</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <v-list dense>
                                                        <v-list-item @click="toggleUpdateComment(task_comment)">
                                                            <v-list-item-icon>
                                                                <v-icon small>mdi-pencil</v-icon>
                                                            </v-list-item-icon>
                                                            <v-list-item-title>Edit</v-list-item-title>
                                                        </v-list-item>
                                                        <v-list-item @click="deleteComment(task_comment)">
                                                            <v-list-item-icon>
                                                                <v-icon small color="error">mdi-delete</v-icon>
                                                            </v-list-item-icon>
                                                            <v-list-item-title>Delete</v-list-item-title>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-menu>
                                            </div>

                                            <div class="comment-body">
                                                <div v-show="task_comment.id != comment_to_edit.id"
                                                    class="comment-content" v-html="task_comment.comment" />

                                                <div v-show="task_comment.id == comment_to_edit.id"
                                                    class="comment-edit">
                                                    <wysiwyg v-model="task_comment.comment"
                                                        :ref="'editorEdit_' + task_comment.id" />
                                                    <div class="comment-edit-actions">
                                                        <v-btn @click="comment_to_edit.id = null; showNewComment = true"
                                                            small text>
                                                            Cancel
                                                        </v-btn>
                                                        <v-btn @click="updateComment(task_comment)" color="primary"
                                                            small>
                                                            <v-icon left small>mdi-content-save</v-icon>
                                                            Update
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Reply button (only show if not the current user's comment) -->
                                            <div v-if="get_user.id !== task_comment.user_id && task_comment.id != comment_to_edit.id"
                                                class="comment-actions">
                                                <v-btn @click="startReply(task_comment)" text small color="primary"
                                                    class="reply-btn">
                                                    <v-icon left small>mdi-reply</v-icon>
                                                    Reply
                                                </v-btn>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </v-tab-item>

                            <v-tab-item>
                                <!-- All Activity Tab Content -->
                                <div class="tab-content">
                                    <div v-if="allActivityItems.length === 0" class="no-activity">
                                        <v-icon color="grey">mdi-timeline</v-icon>
                                        <span>No activity yet</span>
                                    </div>

                                    <div v-else class="activity-list">
                                        <div v-for="item in sortedActivityItems" :key="item.key" class="activity-item"
                                            :class="{ 'is-comment': item.type === 'comment' }">
                                            <!-- Activity Item -->
                                            <div v-if="item.type === 'activity'" class="activity-content">
                                                <div class="activity-header">
                                                    <v-avatar :color="getAvatarColor(item.user?.name || 'Unknown')"
                                                        size="32" class="activity-avatar">
                                                        <span class="avatar-text">{{ getInitials(item.user?.name || 'U')
                                                            }}</span>
                                                    </v-avatar>
                                                    <div class="activity-meta">
                                                        <div class="activity-summary">
                                                            <strong>{{ item.user?.name || 'Unknown User' }}</strong>
                                                            {{ formatActivityDescription(item) }}
                                                        </div>
                                                        <div class="activity-time">
                                                            {{ $date(item.created_at).fromNow() }} •
                                                            {{ $date(item.created_at).format(`MMM DD, YYYY ${$tz.time}`) }}
                                                        </div>
                                                    </div>
                                                    <!-- Hide the edit/pencil icon for 'updated' actions to avoid edit affordance on activity items -->
                                                    <v-icon v-if="item.action !== 'updated'" class="activity-icon"
                                                        :color="getActivityIconColor(item.action)" size="16">
                                                        {{ getActivityIcon(item.action) }}
                                                    </v-icon>
                                                </div>
                                            </div>

                                            <!-- Comment Item -->
                                            <div v-else class="comment-content" :class="{
                                                'reply-comment': item.parent_id,
                                                'highlighted-comment': highlightedComment === item.id
                                            }">
                                                <div class="comment-header">
                                                    <v-avatar :color="getAvatarColor(item.user.name)" size="32"
                                                        class="comment-avatar">
                                                        <span class="avatar-text">{{ getInitials(item.user.name)
                                                            }}</span>
                                                    </v-avatar>
                                                    <div class="comment-meta">
                                                        <div class="comment-author">{{ item.user.name }}</div>
                                                        <div class="comment-time">
                                                            {{ $date(item.created_at).fromNow() }} •
                                                            {{ $date(item.created_at).format(`MMM DD, YYYY ${$tz.time}`) }}
                                                        </div>
                                                    </div>
                                                    <v-spacer />
                                                    <v-menu v-if="get_user.id == item.user_id" bottom left>
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-btn icon small v-bind="attrs" v-on="on">
                                                                <v-icon small>mdi-dots-horizontal</v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <v-list dense>
                                                            <v-list-item @click="toggleUpdateComment(item)">
                                                                <v-list-item-icon>
                                                                    <v-icon small>mdi-pencil</v-icon>
                                                                </v-list-item-icon>
                                                                <v-list-item-title>Edit</v-list-item-title>
                                                            </v-list-item>
                                                            <v-list-item @click="deleteComment(item)">
                                                                <v-list-item-icon>
                                                                    <v-icon small color="error">mdi-delete</v-icon>
                                                                </v-list-item-icon>
                                                                <v-list-item-title>Delete</v-list-item-title>
                                                            </v-list-item>
                                                        </v-list>
                                                    </v-menu>
                                                </div>

                                                <div class="comment-body">
                                                    <div v-show="item.id != comment_to_edit.id" class="comment-text"
                                                        v-html="item.comment" />

                                                    <div v-show="item.id == comment_to_edit.id" class="comment-edit">
                                                        <wysiwyg v-model="item.comment"
                                                            :ref="'editorEdit_' + item.id" />
                                                        <div class="comment-edit-actions">
                                                            <v-btn
                                                                @click="comment_to_edit.id = null; showNewComment = true"
                                                                small text>
                                                                Cancel
                                                            </v-btn>
                                                            <v-btn @click="updateComment(item)" color="primary" small>
                                                                <v-icon left small>mdi-content-save</v-icon>
                                                                Update
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Reply button for comments in activity tab -->
                                                <div v-if="get_user.id !== item.user_id && item.id != comment_to_edit.id"
                                                    class="comment-actions">
                                                    <v-btn @click="startReply(item)" text small color="primary"
                                                        class="reply-btn">
                                                        <v-icon left small>mdi-reply</v-icon>
                                                        Reply
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </v-tab-item>
                        </v-tabs>
                    </div>

                    <v-divider class="section-divider" />

                    <!-- Description Section -->
                    <div class="description-section" data-tour="description-section">
                        <div class="section-header">
                            <h3 class="section-title">
                                <v-icon left>mdi-text</v-icon>
                                Description
                            </h3>
                        </div>
                        <div class="description-content">
                            <wysiwyg v-model="get_selected_task.description" />
                            <v-btn color="primary" small text @click="addDescription" class="save-description-btn">
                                <v-icon left small>mdi-content-save</v-icon>
                                Save Description
                            </v-btn>
                        </div>
                    </div>

                    <v-divider class="section-divider" />

                    <!-- Additional Details Section - Collapsible -->
                    <div class="details-section">
                        <v-expansion-panels flat>
                            <v-expansion-panel>
                                <v-expansion-panel-header class="details-header">
                                    <div class="details-title">
                                        <v-icon left>mdi-information-outline</v-icon>
                                        Additional Details
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <div class="additional-details">
                                        <div class="detail-item">
                                            <div class="detail-label">Created</div>
                                            <div class="detail-value">
                                                {{ $date(get_selected_task.created_at).format("MMM DD, YYYY") }}
                                                <span class="week-info">Week {{
                                                    $date(get_selected_task.created_at).week() }}</span>
                                            </div>
                                        </div>

                                        <div class="detail-item">
                                            <div class="detail-label">Created By</div>
                                            <div class="detail-value">
                                                <div v-if="get_selected_task.creator" class="creator-info">
                                                    <v-avatar size="20"
                                                        :color="getAvatarColor(get_selected_task.creator.name)"
                                                        class="creator-avatar">
                                                        <span class="avatar-text">{{
                                                            getInitials(get_selected_task.creator.name) }}</span>
                                                    </v-avatar>
                                                    <span>{{ get_selected_task.creator.name }}</span>
                                                </div>
                                                <span v-else class="no-value">None</span>
                                            </div>
                                        </div>

                                        <div class="detail-item" v-if="get_selected_task.meeting">
                                            <div class="detail-label">Meeting</div>
                                            <div class="detail-value">
                                                <a @click="openMeeting()" class="meeting-link">
                                                    {{ get_selected_task.meeting.name }}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>

                    <v-divider class="section-divider" />

                    <!-- Subtasks Section -->
                    <div class="subtasks-section" data-tour="subtasks-section">
                        <div class="section-header">
                            <h3 class="section-title">
                                <v-icon left>mdi-format-list-checks</v-icon>
                                Subtasks
                                <v-chip small outlined class="subtask-count">
                                    {{ (get_selected_task.subtasks || []).length }}
                                </v-chip>
                            </h3>
                            <v-btn v-if="!create_quick_task" @click="openCreateQuickTask()" small text color="primary"
                                class="add-subtask-btn">
                                <v-icon small left>mdi-plus</v-icon>
                                Add Subtask
                            </v-btn>
                        </div>

                        <div class="subtasks-content">
                            <v-data-table v-if="get_selected_task.subtasks" :items="get_selected_task.subtasks.filter(
                                x => selected_statuses.includes(x.status_id) && selected_priorities.includes(x.priority_id)
                            )" :headers="subtask_headers" :hide-default-header="get_selected_task.subtasks.length == 0"
                                hide-default-footer disable-pagination class="subtasks-table">
                                <template v-slot:item="{ item }">
                                    <tr class="subtask-row">
                                        <td class="subtask-name">
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-text-field v-model="item.name" @keyup.enter="updateSubtask(item)" dense
                                                        hide-details class="subtask-name-field" placeholder="Subtask name" 
                                                        @blur="updateSubtask(item)" v-bind="attrs" v-on="on" />
                                                </template>
                                                <span>{{ item.name }}</span>
                                            </v-tooltip>
                                        </td>
                                        <td class="subtask-assignee">
                                            <v-select v-model="item.assignee_id" :items="users"
                                                @change="updateSubtask(item)" item-text="name" item-value="id" dense
                                                hide-details class="subtask-select" />
                                        </td>
                                        <td class="subtask-priority">
                                            <v-select v-model="item.priority_id" @change="updateSubtask(item)"
                                                :items="get_priorities" item-text="name" item-value="id" dense
                                                hide-details class="subtask-select chip-field">
                                                <template #selection="{ item }">
                                                    <v-chip x-small :color="getPriorityChipColor(item.id)"
                                                        :text-color="getPriorityTextColor(item.id)">
                                                        {{ item.name }}
                                                    </v-chip>
                                                </template>
                                            </v-select>
                                        </td>
                                        <td class="subtask-status">
                                            <v-select v-model="item.status_id" @change="updateSubtask(item)"
                                                :items="get_statuses" item-text="name" item-value="id" dense
                                                hide-details class="subtask-select chip-field">
                                                <template #selection="{ item }">
                                                    <v-chip x-small :color="getStatusChipColor(item.id)"
                                                        :text-color="getStatusTextColor(item.id)">
                                                        {{ item.name }}
                                                    </v-chip>
                                                </template>
                                            </v-select>
                                        </td>
                                        <td class="subtask-actions">
                                            <v-menu bottom left>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn small icon v-bind="attrs" v-on="on">
                                                        <v-icon small>mdi-dots-vertical</v-icon>
                                                    </v-btn>
                                                </template>
                                                <v-list dense>
                                                    <v-list-item @click="deleteSubtask(item)">
                                                        <v-list-item-icon>
                                                            <v-icon small color="error">mdi-delete</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-title>Delete</v-list-item-title>
                                                    </v-list-item>
                                                </v-list>
                                            </v-menu>
                                        </td>
                                    </tr>
                                </template>

                                <template slot="body.append">
                                    <tr v-if="create_quick_task" class="quick-subtask-row">
                                        <td colspan="5">
                                            <v-text-field v-model="new_subtask.name" ref="quickSubtask"
                                                @blur="closeCreateQuickTask()" @keyup.enter="createSubtask()" dense
                                                hide-details placeholder="Enter subtask name and press Enter"
                                                class="quick-subtask-field" outlined />
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>

                            <div v-if="!get_selected_task.subtasks || get_selected_task.subtasks.length === 0"
                                class="no-subtasks">
                                <v-icon color="grey">mdi-format-list-checks</v-icon>
                                <span>No subtasks yet</span>
                            </div>
                        </div>
                    </div>

                    <v-divider class="section-divider" />

                    <!-- Attachments Section -->
                    <div class="attachments-section" data-tour="attachments-section">
                        <div class="section-header">
                            <h3 class="section-title">
                                <v-icon left>mdi-paperclip</v-icon>
                                Attachments
                                <v-chip small outlined class="attachment-count">
                                    {{ (get_selected_task.attachments || []).length }}
                                </v-chip>
                            </h3>
                            <v-btn small text :color="showAttachResourceDialog ? 'error' : 'primary'"
                                class="add-attachment-btn"
                                @click="showAttachResourceDialog = !showAttachResourceDialog">
                                <v-icon small left>{{ showAttachResourceDialog ? 'mdi-close' : 'mdi-plus' }}</v-icon>
                                {{ showAttachResourceDialog ? 'Cancel' : 'Add Attachment' }}
                            </v-btn>
                        </div>

                        <div class="attachments-content">
                            <!-- Embedded Attach Resource Component -->
                            <div v-if="showAttachResourceDialog" class="mb-4">
                                <AttachResourceDialogEmbedded :embedded="true"
                                    :default-company-id="get_selected_task.company_id"
                                    :already-attached="get_selected_task.attachments || []"
                                    @resources-attached="handleResourcesAttached"
                                    @close="showAttachResourceDialog = false" />
                            </div>

                            <div v-if="get_selected_task.attachments && get_selected_task.attachments.length > 0"
                                class="attachments-list">
                                <div v-for="(attachment, index) in get_selected_task.attachments" :key="attachment.resource_id || index"
                                    class="attachment-item">
                                    <div class="attachment-info">
                                        <v-icon :color="getFileTypeColor(attachment.file_type)" class="attachment-icon">
                                            {{ getFileTypeIcon(attachment.file_type) }}
                                        </v-icon>
                                        <div class="attachment-details">
                                            <div class="attachment-name">{{ attachment.file_name }}</div>
                                            <div class="attachment-meta">
                                                <span v-if="attachment.file_size" class="attachment-size">{{ formatFileSize(attachment.file_size) }}</span>
                                                <span v-if="attachment.category" class="attachment-category">{{
                                                    attachment.category
                                                    }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="attachment-actions">
                                        <v-btn icon small color="primary" @click="viewAttachment(attachment)"
                                            title="View attachment">
                                            <v-icon small>mdi-eye</v-icon>
                                        </v-btn>
                                        <v-btn icon small 
                                            :color="isUrlResource(attachment) ? 'green' : 'blue'" 
                                            @click="downloadAttachment(attachment)"
                                            :title="isUrlResource(attachment) ? 'Open link' : 'Download attachment'">
                                            <v-icon small>{{ isUrlResource(attachment) ? 'mdi-open-in-new' : 'mdi-download' }}</v-icon>
                                        </v-btn>
                                        <v-btn icon small color="red" @click="removeAttachment(attachment.resource_id || index)"
                                            title="Remove attachment">
                                            <v-icon small>mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>

                            <div v-else-if="!showAttachResourceDialog" class="no-attachments">
                                <v-icon color="grey">mdi-paperclip</v-icon>
                                <span>No attachments yet</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Comment Input Footer -->
                <div class="comment-footer" v-show="showNewComment" data-tour="comment-footer">
                    <!-- Reply indicator -->
                    <div v-if="replyingTo" class="reply-indicator">
                        <div class="reply-header">
                            <v-icon small color="primary">mdi-reply</v-icon>
                            <span class="reply-text">Replying to <strong>{{ replyingTo.user.name }}</strong></span>
                            <v-btn icon small @click="cancelReply" class="cancel-reply-btn">
                                <v-icon small>mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <div class="reply-preview" v-html="replyingTo.comment"></div>
                    </div>

                    <div v-if="!isCommentInputVisible" class="add-comment-prompt" @click="isCommentInputVisible = true">
                        <v-avatar :color="getAvatarColor(get_user.name)" size="28" class="mr-2">
                            <span class="avatar-text">{{ getInitials(get_user.name) }}</span>
                        </v-avatar>
                        <span class="add-comment-placeholder">{{ replyingTo ? 'Write a reply...' : 'Add a comment...' }}</span>
                        <v-btn color="primary" small rounded class="add-comment-post-btn"
                            @click.stop="isCommentInputVisible = true">
                            {{ replyingTo ? 'Reply' : 'Post' }}
                        </v-btn>
                    </div>

                    <div v-if="isCommentInputVisible" class="comment-input-section">
                        <div class="comment-input-header">
                            <v-btn text @click="isCommentInputVisible = false" color="primary" small>
                                <v-icon left>mdi-chevron-up</v-icon>
                                Collapse
                            </v-btn>
                        </div>

                        <wysiwyg v-model="new_task_comment" placeholder="Add a comment..." ref="editor" />

                        <!-- Mention Autocomplete Dropdown -->
                        <div v-if="showMentionDropdown" class="mention-dropdown" :style="{
                            top: mentionDropdownPosition.top + 'px',
                            left: mentionDropdownPosition.left + 'px'
                        }">
                            <div v-if="filteredMentionUsers.length === 0" class="mention-dropdown-item no-results">
                                No users found
                            </div>
                            <div v-else
                                v-for="(user, index) in filteredMentionUsers.slice(0, 5)"
                                :key="user.id"
                                class="mention-dropdown-item"
                                :class="{ 'mention-dropdown-item-selected': index === selectedMentionIndex }"
                                @click="selectMentionUser(user)"
                                @mouseenter="selectedMentionIndex = index"
                            >
                                <v-avatar :color="getAvatarColor(user.name)" size="24" class="mr-2">
                                    <span class="avatar-text white--text" style="font-size: 10px;">{{ getInitials(user.name) }}</span>
                                </v-avatar>
                                <div class="mention-user-info">
                                    <div class="mention-user-name">{{ user.name }}</div>
                                    <div class="mention-user-email" v-if="user.email">{{ user.email }}</div>
                                </div>
                            </div>
                        </div>

                        <div class="comment-submit-actions">
                            <v-btn @click="createComment()" color="primary" small rounded>
                                <v-icon left small>mdi-send</v-icon>
                                {{ replyingTo ? 'Reply' : 'Post' }}
                            </v-btn>
                        </div>
                    </div>
                </div>
            </div>
        </v-navigation-drawer>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import AttachResourceDialogEmbedded from './AttachResourceDialogEmbedded.vue';

export default {
    name: "TaskNavigationComponent",
    components: {
        AttachResourceDialogEmbedded
    },
    inject: ['companies', 'refreshTasks', 'updateTaskInArrays', 'addPersonalTask', 'addAssignedTask', 'optimisticUpdateTask', 'optimisticDeleteTask', 'personalTasks', 'assignedTasks'],
    props: ["task_details_dialog", "can_close_task_details_dialog", "users"],
    data: () => ({
        loading: false,
        api_url: null,
        due_date_menu: false,
        start_time_menu: false,
        end_time_menu: false,
        seriesStartDateMenu: false,
        seriesEndDateMenu: false,
        isCommentInputVisible: false,
        statusSelectKey: 0,
        originalTaskName: null,
        new_subtask: {
            name: null,
            description: null,
            priority_id: null,
            due_date: null,
        },
        subtask_headers: [
            { text: "Task Name", value: "name", width: "40%" },
            { text: "Assignee", value: "assignee_id", width: "30%" },
            { text: "Priority", value: "priority_id", width: "15%" },
            { text: "Status", value: "status_id", width: "15%" },
            { text: "", width: "1%", sortable: false },
        ],
        navigation: {
            width: 700,
            borderSize: 5,
        },
        show_filters_dialog: false,
        create_quick_task: false,
        new_task_comment: null,
        daysOfWeek: [
            { label: 'Mon', value: 1 },
            { label: 'Tue', value: 2 },
            { label: 'Wed', value: 3 },
            { label: 'Thu', value: 4 },
            { label: 'Fri', value: 5 },
            { label: 'Sat', value: 6 },
            { label: 'Sun', value: 0 }
        ],
        comment_to_edit: {
            id: null,
        },
        showNewComment: true,
        activeActivityTab: 0, // 0 = Comments, 1 = All Activity
        activitySortOrder: 'newest', // 'newest' or 'oldest'
        // Reply functionality
        replyingTo: null, // Comment being replied to
        highlightedComment: null, // Comment to highlight when replying
        // Attachment functionality
        showAttachResourceDialog: false,
        // Mention autocomplete
        showMentionDropdown: false,
        mentionSearch: '',
        mentionDropdownPosition: { top: 0, left: 0 },
        selectedMentionIndex: 0,
        mentionRange: null, // Store the text range for @ insertion
        sendingAssigneeReminder: false,
    }),
    created() {
        this.api_url = process.env.VUE_APP_API_URL;
    },
    mounted() {
        this.loading = true;
        // this.setBorderWidth()
        // this.setEvents()
        
        // Attach mention autocomplete listeners to WYSIWYG editor
        this.$nextTick(() => {
            this.attachMentionListeners();
        });
    },

    beforeDestroy() {
        // Clean up mention listeners
        this.removeMentionListeners();
    },

    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_selected_company: "companies/get_selected_company",
            get_priorities: "tasks/get_priorities",
            get_statuses: "tasks/get_statuses",
            get_selected_priorities: "tasks/get_selected_priorities",
            get_selected_statuses: "tasks/get_selected_statuses",
            get_selected_task: "tasks/get_selected_task",
        }),
        get_companies() {
            return this.companies ? this.companies() : [];
        },

        // Show the follow-up reminder button only when the current user is the
        // task creator (or admin/admin-manager) and the task has assignees
        // other than the creator themselves.
        canSendAssigneeReminder() {
            const task = this.get_selected_task;
            if (!task || !task.id) return false;
            const userId = this.get_user && this.get_user.id;
            if (!userId) return false;
            const isCreator = task.user_id === userId;
            const isAdmin = [1, 11].includes(this.get_user.role_id);
            if (!isCreator && !isAdmin) return false;
            const assignees = Array.isArray(task.assignees) ? task.assignees : [];
            return assignees.some(a => a && a.user_id && a.user_id !== userId);
        },

        // Extract unique tags from all injected tasks
        availableTags() {
            const allTags = new Set();

            // Get tags from personal tasks
            if (this.personalTasks) {
                const personalTasks = this.personalTasks();
                if (Array.isArray(personalTasks)) {
                    personalTasks.forEach(task => {
                        if (task.tags && Array.isArray(task.tags)) {
                            task.tags.forEach(tag => {
                                if (tag && typeof tag === 'string') {
                                    allTags.add(tag);
                                } else if (tag && tag.name) {
                                    allTags.add(tag.name);
                                }
                            });
                        }
                    });
                }
            }

            // Get tags from assigned tasks
            if (this.assignedTasks) {
                const assignedTasks = this.assignedTasks();
                if (Array.isArray(assignedTasks)) {
                    assignedTasks.forEach(task => {
                        if (task.tags && Array.isArray(task.tags)) {
                            task.tags.forEach(tag => {
                                if (tag && typeof tag === 'string') {
                                    allTags.add(tag);
                                } else if (tag && tag.name) {
                                    allTags.add(tag.name);
                                }
                            });
                        }
                    });
                }
            }

            // Convert back to array of unique tag strings and sort
            return Array.from(allTags).sort();
        },
        selected_priorities: {
            get() {
                return this.get_selected_priorities;
            },
            set(new_priorities) {
                this.$store.dispatch("tasks/set_selected_priorities", new_priorities);
            },
        },
        selected_statuses: {
            get() {
                return this.get_selected_statuses;
            },
            set(new_statuses) {
                this.$store.dispatch("tasks/set_selected_statuses", new_statuses);
            },
        },
        assignedUser() {
            if (!this.get_selected_task?.assignees) return [];
            return this.get_selected_task.assignees.map(assignee => assignee.user).filter(Boolean);
        },
        // Activity timeline computed properties
        allActivityItems() {
            const activities = [];

            // Add task activities (check both possible property names)
            const taskActivities = this.get_selected_task.taskActivities || this.get_selected_task.task_activities;
            if (taskActivities && Array.isArray(taskActivities)) {
                taskActivities.forEach(activity => {
                    activities.push({
                        ...activity,
                        type: 'activity',
                        key: `activity-${activity.id}`
                    });
                });
            }

            // Add comments
            if (this.get_selected_task.task_comments) {
                this.get_selected_task.task_comments.forEach(comment => {
                    activities.push({
                        ...comment,
                        type: 'comment',
                        key: `comment-${comment.id}`
                    });
                });
            }

            return activities;
        },
        sortedComments() {
            if (!this.get_selected_task.task_comments) return [];
            const comments = [...this.get_selected_task.task_comments];
            return this.activitySortOrder === 'newest'
                ? comments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                : comments.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        },

        // Organize comments with their replies
        organizedComments() {
            if (!this.get_selected_task.task_comments) return [];

            // console.log('[organizedComments] All comments:', this.get_selected_task.task_comments.map(c => ({
            //     id: c.id,
            //     parent_id: c.parent_id,
            //     created_at: c.created_at,
            //     comment: c.comment.substring(0, 20) + '...'
            // })));

            const allComments = [...this.get_selected_task.task_comments];
            const parentComments = allComments.filter(comment => !comment.parent_id);
            const replyComments = allComments.filter(comment => comment.parent_id);

            // console.log('[organizedComments] Parent comments:', parentComments.length);
            // console.log('[organizedComments] Reply comments:', replyComments.length);
            // console.log('[organizedComments] Sort order:', this.activitySortOrder);

            // Sort parent comments
            const sortedParents = this.activitySortOrder === 'newest'
                ? parentComments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                : parentComments.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

            // Build organized structure with proper threading
            const organized = [];
            sortedParents.forEach(parent => {
                organized.push(parent);

                // Find and sort replies for this parent
                const replies = replyComments.filter(reply => reply.parent_id === parent.id);
                // For replies, sort newest first (reverse chronological order) to match conversation flow
                const sortedReplies = replies.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                // console.log(`[organizedComments] Parent ${parent.id} has ${replies.length} replies`);
                // if (replies.length > 0) {
                //     console.log('[organizedComments] Replies for parent:', replies.map(r => ({
                //         id: r.id,
                //         parent_id: r.parent_id,
                //         created_at: r.created_at
                //     })));
                // }

                organized.push(...sortedReplies);
            });

            // console.log('[organizedComments] Final organized count:', organized.length);

            return organized;
        },
        sortedActivityItems() {
            const items = [...this.allActivityItems];
            return this.activitySortOrder === 'newest'
                ? items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                : items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        },

        // Helper method to determine if an attachment is a URL resource
        isUrlResource() {
            return (attachment) => {
                return attachment && (attachment.type === 'url' || (attachment.file_url && (attachment.file_url.startsWith('http://') || attachment.file_url.startsWith('https://')) && !attachment.file_path));
            };
        },

        // Filtered users for mention autocomplete
        filteredMentionUsers() {
            if (!this.mentionSearch || !this.users) return this.users || [];
            const search = this.mentionSearch.toLowerCase();
            return this.users.filter(user => 
                user.name.toLowerCase().includes(search) || 
                (user.email && user.email.toLowerCase().includes(search))
            );
        },
    },
    watch: {
        get_selected_task: {
            handler(newTask) {
                // Store the original task name when task selection changes
                if (newTask && newTask.name) {
                    this.originalTaskName = newTask.name;
                }
            },
            immediate: true
        },
        isCommentInputVisible(newVal) {
            if (newVal) {
                // Reattach listeners when comment input becomes visible
                this.$nextTick(() => {
                    this.attachMentionListeners();
                });
            }
        }
    },
    methods: {
        async sendAssigneeReminder() {
            const task = this.get_selected_task;
            if (!task || !task.id || this.sendingAssigneeReminder) return;

            const assigneeNames = (task.assignees || [])
                .map(a => (a.user && a.user.name) || '')
                .filter(Boolean)
                .join(', ');

            const confirmMsg = assigneeNames
                ? `Send a follow-up reminder email to: ${assigneeNames}?`
                : 'Send a follow-up reminder to assignees?';
            if (!confirm(confirmMsg)) return;

            this.sendingAssigneeReminder = true;
            try {
                const response = await this.$axios.post(`api/tasks/send_assignee_reminder/${task.id}`);
                if (response && response.data && response.data.response) {
                    this.$toast.success(response.data.message || 'Reminder sent.');
                } else {
                    this.$toast.error((response && response.data && response.data.message) || 'Failed to send reminder');
                }
            } catch (error) {
                this.$toast.error((error.response && error.response.data && error.response.data.message) || error.message || 'Failed to send reminder');
            } finally {
                this.sendingAssigneeReminder = false;
            }
        },

        getAssigneeIds() {
            if (!this.get_selected_task?.assignees) return [];
            return this.get_selected_task.assignees.map(assignee =>
                assignee.user_id || assignee.user?.id || assignee.id
            );
        },

        async updateTaskAssignees(assigneeIds) {
            // Debug logging to understand task object structure
            // console.log('[TaskNavigation] updateTaskAssignees called with:', assigneeIds);
            // console.log('[TaskNavigation] Current get_selected_task:', this.get_selected_task);

            // Validate task object
            if (!this.get_selected_task || !this.get_selected_task.id) {
                console.error("Cannot update assignees: no selected task or missing ID", this.get_selected_task);
                this.$toast.error("Cannot update assignees: no task selected");
                return;
            }

            // Use DefaultLayout's optimistic update if available
            if (this.optimisticUpdateTask) {
                try {
                    const newAssignees = assigneeIds.map(id => {
                        const user = this.users.find(u => u.id === id);
                        return { user_id: id, user: user };
                    });

                    await this.optimisticUpdateTask(
                        this.get_selected_task.id,
                        { assignees: newAssignees },
                        async () => {
                            const payload = {
                                assignee_ids: assigneeIds || []
                            };
                            const response = await this.$axios.post(`api/tasks/manage_assignees/${this.get_selected_task.id}`, payload);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                    this.$toast.success("Assignees updated successfully");
                } catch (error) {
                    console.error("Error updating assignees:", error);
                    this.$toast.error(error.message || "Failed to update assignees");
                }
            } else {
                // Fallback to local optimistic update
                // Store previous assignees for rollback
                const previousAssignees = [...(this.get_selected_task.assignees || [])];

                // Optimistic update - immediately update the UI
                this.get_selected_task.assignees = assigneeIds.map(id => {
                    const user = this.users.find(u => u.id === id);
                    return { user_id: id, user: user };
                });

                try {
                    const payload = {
                        assignee_ids: assigneeIds || []
                    };

                    const response = await this.$axios.post(`api/tasks/manage_assignees/${this.get_selected_task.id}`, payload);
                    if (response.data.response) {
                        // Success - optimistic update was correct
                        // console.log("Assignees updated successfully");
                        this.$toast.success("Assignees updated successfully");

                        // Emit update to parent component for optimistic stats
                        this.$emit('updateTask', this.get_selected_task);

                        // Refresh tasks using injected method
                        if (typeof this.refreshTasks === 'function') {
                            await this.refreshTasks();
                        }
                    } else {
                        // Server error - rollback optimistic update
                        this.get_selected_task.assignees = previousAssignees;
                        this.$toast.error(response.data.message);
                    }
                } catch (error) {
                    // Network error - rollback optimistic update
                    this.get_selected_task.assignees = previousAssignees;
                    console.error("Error updating assignees:", error);
                    this.$toast.error("Failed to update assignees");
                }
            }
        },


        showSnackBar(message) {
            this.$store.commit(
                "auth/setMessage",
                { show: true, message: message },
                { root: 1 }
            );
        },

        closeTaskDetailsDialog() {
            this.$emit("closeTaskDetailsDialog");
        },

        toggleRecurrenceDay(day) {
            // Ensure the task exists and is recurring
            if (!this.get_selected_task || this.get_selected_task.status_id !== 7) {
                return;
            }
            
            // Initialize recurrence fields if they don't exist
            if (!this.get_selected_task.recurrence_interval) {
                this.$set(this.get_selected_task, 'recurrence_interval', 1);
                this.$set(this.get_selected_task, 'series_start_date', this.formatDateValue(this.get_selected_task.due_date) || this.$dateApi().format('YYYY-MM-DD'));
                this.$set(this.get_selected_task, 'series_end_date', null);
            }
            
            if (!this.get_selected_task.recurrence_days) {
                this.$set(this.get_selected_task, 'recurrence_days', []);
            }
            
            const index = this.get_selected_task.recurrence_days.indexOf(day);
            if (index > -1) {
                this.get_selected_task.recurrence_days.splice(index, 1);
            } else {
                this.get_selected_task.recurrence_days.push(day);
            }
            
            // Save changes to API
            this.updateTask();
        },

        formatRecurrencePattern(task) {
            // Ensure task exists and is a recurring task
            if (!task || task.status_id !== 7) return 'No recurrence';
            
            // Initialize recurrence fields if they don't exist
            if (!task.recurrence_interval) {
                this.$set(task, 'recurrence_interval', 1);
                this.$set(task, 'recurrence_days', []);
                this.$set(task, 'series_start_date', this.formatDateValue(task.due_date) || this.$dateApi().format('YYYY-MM-DD'));
                this.$set(task, 'series_end_date', null);
            }
            
            let pattern = task.recurrence_interval === 1 ? 'Weekly' : `Every ${task.recurrence_interval} weeks`;
            
            if (task.recurrence_days && task.recurrence_days.length > 0) {
                // Sort days in proper week order (Mon=1, Tue=2, ..., Sun=0 comes last)
                const sortedDays = [...task.recurrence_days].sort((a, b) => {
                    // Convert Sunday (0) to 7 for proper sorting
                    const dayA = a === 0 ? 7 : a;
                    const dayB = b === 0 ? 7 : b;
                    return dayA - dayB;
                });
                
                // Check if it's daily (Mon-Fri) or all days
                const weekdays = [1, 2, 3, 4, 5]; // Mon-Fri
                const allDays = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun
                
                const isWeekdaysOnly = weekdays.every(day => sortedDays.includes(day)) && sortedDays.length === 5;
                const isAllDays = allDays.every(day => sortedDays.includes(day)) && sortedDays.length === 7;
                
                if (isAllDays) {
                    pattern = task.recurrence_interval === 1 ? 'Daily' : `Every ${task.recurrence_interval} weeks (Daily)`;
                } else if (isWeekdaysOnly) {
                    pattern = task.recurrence_interval === 1 ? 'Daily (Weekdays)' : `Every ${task.recurrence_interval} weeks (Weekdays)`;
                } else {
                    const dayNames = sortedDays.map(dayValue => {
                        const day = this.daysOfWeek.find(d => d.value === dayValue);
                        return day ? day.label : dayValue;
                    });
                    pattern += ` on ${dayNames.join(', ')}`;
                }
            }
            
            if (task.series_start_date && task.series_end_date) {
                pattern += ` from ${this.$date(task.series_start_date).format('MMM D, YYYY')} to ${this.$date(task.series_end_date).format('MMM D, YYYY')}`;
            } else if (task.series_start_date) {
                pattern += ` starting ${this.$date(task.series_start_date).format('MMM D, YYYY')}`;
            }
            
            return pattern;
        },

        formatDateValue(dateValue) {
            if (!dateValue) return null;
            
            // If it's already in YYYY-MM-DD format, return as is
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            
            // Otherwise, parse with dayjs and format
            const parsedDate = this.$date(dateValue);
            return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
        },

        updateSeriesStartDate(date) {
            this.get_selected_task.series_start_date = this.formatDateValue(date);
            this.seriesStartDateMenu = false;
            this.updateTask();
        },

        updateSeriesEndDate(date) {
            this.get_selected_task.series_end_date = this.formatDateValue(date);
            this.seriesEndDateMenu = false;
            this.updateTask();
        },

        clearSeriesEndDate() {
            this.get_selected_task.series_end_date = null;
            this.updateTask();
        },

        selectWeekdays() {
            // Select Monday through Friday (1-5)
            if (!this.get_selected_task.recurrence_days) {
                this.$set(this.get_selected_task, 'recurrence_days', []);
            }
            this.get_selected_task.recurrence_days = [1, 2, 3, 4, 5];
            this.updateTask();
        },

        clearAllDays() {
            if (!this.get_selected_task.recurrence_days) {
                this.$set(this.get_selected_task, 'recurrence_days', []);
            }
            this.get_selected_task.recurrence_days = [];
            this.updateTask();
        },

        async updateTaskStatus(newStatus) {
            // Initialize recurrence defaults right when the user actually picks
            // Recurring from this dropdown (a real status change), not via a
            // watcher on get_selected_task.status_id — that fired any time
            // *selection* moved to a different, already-recurring task (old/new
            // status compared across two different tasks, not a real transition),
            // silently patching recurrence_interval onto that task's shared array
            // object. That made the "Weekly" chip on the task list only appear
            // after opening a recurring task in this sidebar, and only for the
            // rest of the session (never persisted, since it happened outside
            // any save flow).
            if (newStatus === 7 && this.get_selected_task && !this.get_selected_task.recurrence_interval) {
                this.$set(this.get_selected_task, 'recurrence_interval', 1);
                this.$set(this.get_selected_task, 'recurrence_days', []);
                this.$set(this.get_selected_task, 'series_start_date', this.formatDateValue(this.get_selected_task.due_date) || this.$dateApi().format('YYYY-MM-DD'));
                this.$set(this.get_selected_task, 'series_end_date', null);
            }

            // Don't mutate get_selected_task.status_id here — it's the same object
            // reference sitting in DefaultLayout's task arrays, and updateTaskInArrays
            // needs to see the *old* status to detect a completed-state transition
            // (so it can move the task into/out of the completed list). Pass the new
            // status through as an override instead; the array gets updated once the
            // server responds, via updateTaskInArrays below.
            await this.updateTask({ status_id: newStatus });
        },
        onTaskNameBlur() {
            // Only update if the task name actually changed
            if (!this.get_selected_task || !this.get_selected_task.name) {
                return;
            }
            
            // Compare with original name to avoid unnecessary updates
            if (this.get_selected_task.name.trim() !== this.originalTaskName?.trim()) {
                this.updateTask();
                // Update the original name after update
                this.originalTaskName = this.get_selected_task.name;
            }
        },
        async updateTask(overrides = {}) {
            // Basic validation
            if (!this.get_selected_task || !this.get_selected_task.id) {
                console.error("Cannot update task: no selected task or missing ID");
                this.$toast.error("Cannot update task: no task selected");
                return;
            }

            const statusId = overrides.status_id !== undefined ? overrides.status_id : this.get_selected_task.status_id;

            // Prepare payload (same shape as DefaultLayout.updateTask)
            const payload = {
                company_id: this.get_selected_task.company_id,
                assignee_id: this.get_selected_task.assignee_id,
                project_id: this.get_selected_task.project_id,
                name: this.get_selected_task.name,
                status_id: statusId,
                description: this.get_selected_task.description,
                priority_id: this.get_selected_task.priority_id,
                due_date: this.get_selected_task.due_date,
                start_time: this.get_selected_task.start_time,
                end_time: this.get_selected_task.end_time,
                allocated_time: this.get_selected_task.allocated_time,
                tags: this.get_selected_task.tags,
                // Include attachments to ensure they persist
                attachments: (this.get_selected_task.attachments || []).map(a => a.url).filter(Boolean),
                // Include recurrence fields if status is recurring (7)
                ...(statusId === 7 && {
                    recurrence_interval: this.get_selected_task.recurrence_interval,
                    recurrence_days: this.get_selected_task.recurrence_days,
                    series_start_date: this.formatDateValue(this.get_selected_task.series_start_date),
                    series_end_date: this.formatDateValue(this.get_selected_task.series_end_date)
                })
            };

            try {
                const response = await this.$axios.post(`api/tasks/update_task/${this.get_selected_task.id}`, payload);

                if (response.data.response) {
                    this.$toast.success(response.data.message || 'Task updated successfully');

                    // If server returned full task data, update the selected task in Vuex so components (including this one) get task_activities
                    if (response.data.data) {
                        // Update Vuex selected task
                        this.$store.dispatch('tasks/set_selected_task', response.data.data);

                        // Also update the task in the centralized arrays via injected helper (if available)
                        if (typeof this.updateTaskInArrays === 'function') {
                            this.updateTaskInArrays(this.get_selected_task.id, response.data.data);
                        }
                    } else {
                        // Fallback: emit to parent if it prefers to handle the update
                        this.$emit('updateTask', this.get_selected_task);
                    }
                } else {
                    this.$toast.error(response.data.message || 'Failed to update task');
                }
            } catch (error) {
                console.error('Error updating task:', error);
                this.$toast.error(error.message || 'Failed to update task');
            }
        },

        async updateTaskTiming() {
            // Auto-calculate allocated_time when start/end times change
            if (this.get_selected_task.start_time && this.get_selected_task.end_time) {
                const startTime = new Date(this.get_selected_task.start_time);
                const endTime = new Date(this.get_selected_task.end_time);

                // Warn if end time is before start time but still allow update
                if (endTime < startTime) {
                    this.$toast.warning('Warning: End time is before start time');
                    // Clear allocated_time if times are invalid
                    this.get_selected_task.allocated_time = null;
                } else {
                    // Calculate duration in HH:mm:ss format
                    const durationMs = endTime - startTime;
                    const hours = Math.floor(durationMs / (1000 * 60 * 60));
                    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

                    // Update allocated_time
                    this.get_selected_task.allocated_time = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                }
            }

            // Always update the task, even if times are cleared
            await this.updateTask();
        },

        formatAllocatedTime() {
            if (!this.get_selected_task.allocated_time) {
                if (this.get_selected_task.start_time && this.get_selected_task.end_time) {
                    const startTime = new Date(this.get_selected_task.start_time);
                    const endTime = new Date(this.get_selected_task.end_time);
                    const durationMs = endTime - startTime;
                    const hours = Math.floor(durationMs / (1000 * 60 * 60));
                    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                    return `${hours}h ${minutes}m`;
                }
                return 'Not set';
            }

            // Parse HH:mm:ss format
            const [hours, minutes] = this.get_selected_task.allocated_time.split(':');
            const h = parseInt(hours) || 0;
            const m = parseInt(minutes) || 0;

            if (h === 0 && m === 0) return 'Not set';
            if (h === 0) return `${m}m`;
            if (m === 0) return `${h}h`;
            return `${h}h ${m}m`;
        },

        formatDateTime(dateTime) {
            if (!dateTime) return null;
            try {
                return this.$date(dateTime).format(`MMM DD, YYYY ${this.$tz.time}`);
            } catch (error) {
                console.error('Error formatting datetime:', error);
                return dateTime;
            }
        },

        openMeeting() {
            this.$store.dispatch(
                "companies/set_selected_company",
                this.get_selected_task.company
            );
            this.$router.push({
                name: "ViewMeeting",
                params: { id: this.get_selected_task.meeting_id },
            });
        },
        onCloseConditional() {
            // Check if tour is active and prevent closing during tour
            if (document.body.classList.contains('tour-mode-active')) {
                return false; // Don't allow closing during tour
            }
            
            // Check if we're inside a v-dialog - if so, disable click-outside to prevent conflicts
            const activeDialog = document.querySelector('.v-dialog.v-dialog--active');
            if (activeDialog) {
                return false; // Don't close when inside a dialog
            }
            
            return this.task_details_dialog && this.can_close_task_details_dialog;
        },
        includeFunction() {
            const tempArray = [];

            // Safely add elements that exist
            const includedElement = document.querySelector(".included");
            if (includedElement) {
                tempArray.push(includedElement);
            }

            document.querySelectorAll(".v-select-list").forEach((select) => {
                if (select) {
                    tempArray.push(select);
                }
            });

            const picker = document.querySelector(".v-picker");
            if (picker) {
                tempArray.push(picker);
            }

            // Add menu overlays and dropdowns
            document.querySelectorAll(".v-menu__content").forEach((menu) => {
                if (menu) {
                    tempArray.push(menu);
                }
            });

            // Add wysiwyg editor elements to prevent click-outside from blocking them
            document.querySelectorAll(".editr").forEach((editor) => {
                if (editor) {
                    tempArray.push(editor);
                }
            });

            document.querySelectorAll(".editr--toolbar").forEach((toolbar) => {
                if (toolbar) {
                    tempArray.push(toolbar);
                }
            });

            document.querySelectorAll(".editr--content").forEach((content) => {
                if (content) {
                    tempArray.push(content);
                }
            });

            // Add dialog elements to prevent issues when navigation drawer is opened from within a dialog
            document.querySelectorAll(".v-dialog").forEach((dialog) => {
                if (dialog) {
                    tempArray.push(dialog);
                }
            });

            document.querySelectorAll(".v-dialog__content").forEach((dialogContent) => {
                if (dialogContent) {
                    tempArray.push(dialogContent);
                }
            });

            return tempArray;
        },
        openCreateQuickTask() {
            this.create_quick_task = true;
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$refs.quickSubtask.$refs.input.focus();
                });
            });
        },
        closeCreateQuickTask() {
            this.create_quick_task = false;
            this.new_subtask = {
                name: null,
                description: null,
                priority_id: null,
                due_date: null,
            };
        },
        async createSubtask() {
            if (!this.new_subtask.name?.trim()) {
                this.$toast.error('Please enter a subtask name');
                return;
            }

            // Create temp subtask with temp ID for optimistic UI
            const tempId = `temp_${Date.now()}`;
            const tempSubtask = {
                id: tempId,
                task_id: this.get_selected_task.id,
                assignee_id: this.get_user.id,
                name: this.new_subtask.name.trim(),
                description: this.new_subtask.description || null,
                priority_id: this.new_subtask.priority_id || 2, // Default to Normal priority
                due_date: this.new_subtask.due_date || null,
                status_id: 1, // Default to 'To Do'
                created_at: new Date().toISOString(),
                user: this.get_user
            };

            // Store form data for API call and potential rollback
            const subtaskData = { ...this.new_subtask };

            // Clear form immediately for better UX
            this.new_subtask = {
                name: null,
                description: null,
                priority_id: null,
                due_date: null,
            };
            this.create_quick_task = false;

            // Optimistic update - add to UI immediately
            if (!this.get_selected_task.subtasks) {
                this.get_selected_task.subtasks = [];
            }
            this.get_selected_task.subtasks.unshift(tempSubtask);

            // Also update in main task arrays via updateTaskInArrays
            if (this.updateTaskInArrays) {
                this.updateTaskInArrays(this.get_selected_task.id, {
                    subtasks: [...this.get_selected_task.subtasks]
                });
            }

            try {
                // API call to create subtask
                const payload = {
                    task_id: this.get_selected_task.id,
                    assignee_id: this.get_user.id,
                    name: subtaskData.name,
                    description: subtaskData.description,
                    priority_id: subtaskData.priority_id || 2,
                    due_date: subtaskData.due_date,
                };

                const response = await this.$axios.post("api/tasks/create_subtask", payload);

                if (response.data.response) {
                    // Replace temp subtask with real one from server
                    const subtaskIndex = this.get_selected_task.subtasks.findIndex(s => s.id === tempId);
                    if (subtaskIndex !== -1 && response.data.data.subtasks) {
                        // Find the newly created subtask (likely the last or newest one)
                        const newSubtask = response.data.data.subtasks.find(s =>
                            s.name === subtaskData.name && !this.get_selected_task.subtasks.some(existing => existing.id === s.id)
                        ) || response.data.data.subtasks[response.data.data.subtasks.length - 1];

                        this.get_selected_task.subtasks[subtaskIndex] = newSubtask;

                        // Sync to main task arrays with real data
                        if (this.updateTaskInArrays) {
                            this.updateTaskInArrays(this.get_selected_task.id, {
                                subtasks: response.data.data.subtasks
                            });
                        }
                    }

                    this.$toast.success(response.data.message || 'Subtask created successfully');

                    // Update Vuex with full task data from server
                    this.$store.dispatch("tasks/set_selected_task", response.data.data);
                } else {
                    throw new Error(response.data.message || 'Failed to create subtask');
                }
            } catch (error) {
                // Rollback - remove temp subtask from UI
                const subtaskIndex = this.get_selected_task.subtasks.findIndex(s => s.id === tempId);
                if (subtaskIndex !== -1) {
                    this.get_selected_task.subtasks.splice(subtaskIndex, 1);

                    // Rollback in main task arrays
                    if (this.updateTaskInArrays) {
                        this.updateTaskInArrays(this.get_selected_task.id, {
                            subtasks: [...this.get_selected_task.subtasks]
                        });
                    }
                }

                // Restore form data
                this.new_subtask = subtaskData;
                this.create_quick_task = true;

                console.error('[TaskNavigation] Error creating subtask:', error);
                this.$toast.error(error.message || 'Failed to create subtask');
            }
        },
        async getTasks() {
            // Use injected method instead of making direct API calls
            // console.log('[TaskNavigation] Delegating task refresh to DefaultLayout');
            if (typeof this.refreshTasks === 'function') {
                await this.refreshTasks();
            } else {
                console.warn('[TaskNavigation] refreshTasks method not available from injection');
            }
        },
        async updateSubtask(item) {
            // Validate subtask
            if (!item || !item.id) {
                this.$toast.error('Invalid subtask');
                return;
            }

            // Find subtask index in parent task's subtasks array
            const subtaskIndex = this.get_selected_task.subtasks?.findIndex(s => s.id === item.id);
            if (subtaskIndex === -1 || subtaskIndex === undefined) {
                this.$toast.error('Subtask not found');
                return;
            }

            // Store original for rollback
            const originalSubtask = { ...this.get_selected_task.subtasks[subtaskIndex] };

            // Prepare update payload
            const subtaskUpdates = {
                assignee_id: item.assignee_id,
                name: item.name,
                status_id: item.status_id,
                description: item.description,
                priority_id: item.priority_id,
                due_date: item.due_date,
                allocated_time: item.allocated_time,
            };

            // Optimistic update - apply changes immediately
            Object.assign(this.get_selected_task.subtasks[subtaskIndex], subtaskUpdates);

            // Also update in main task arrays via updateTaskInArrays
            if (this.updateTaskInArrays) {
                this.updateTaskInArrays(this.get_selected_task.id, {
                    subtasks: [...this.get_selected_task.subtasks]
                });
            }

            try {
                // API call to persist changes
                const payload = {
                    task_id: item.task_id,
                    ...subtaskUpdates,
                };
                const response = await this.$axios.post(`api/tasks/update_subtask/${item.id}`, payload);

                if (response.data.response) {
                    this.$toast.success(response.data.message || 'Subtask updated successfully');
                } else {
                    throw new Error(response.data.message || 'Failed to update subtask');
                }
            } catch (error) {
                // Rollback on error - restore original subtask
                Object.assign(this.get_selected_task.subtasks[subtaskIndex], originalSubtask);

                // Rollback in main task arrays
                if (this.updateTaskInArrays) {
                    this.updateTaskInArrays(this.get_selected_task.id, {
                        subtasks: [...this.get_selected_task.subtasks]
                    });
                }

                console.error('[TaskNavigation] Error updating subtask:', error);
                this.$toast.error(error.message || 'Failed to update subtask');
            }
        },
        async deleteSubtask(item) {
            // Validate subtask
            if (!item || !item.id) {
                this.$toast.error('Invalid subtask');
                return;
            }

            const subtaskIndex = this.get_selected_task.subtasks?.findIndex(s => s.id === item.id);
            if (subtaskIndex === -1 || subtaskIndex === undefined) {
                this.$toast.error('Subtask not found');
                return;
            }

            // Store original subtask for rollback
            const originalSubtask = { ...this.get_selected_task.subtasks[subtaskIndex] };

            // Optimistic delete - remove from UI immediately
            this.get_selected_task.subtasks.splice(subtaskIndex, 1);

            // Also update in main task arrays via updateTaskInArrays
            if (this.updateTaskInArrays) {
                this.updateTaskInArrays(this.get_selected_task.id, {
                    subtasks: [...this.get_selected_task.subtasks]
                });
            }

            try {
                const response = await this.$axios.delete(`api/tasks/delete_subtask/${item.id}`);

                if (response.data.response) {
                    this.$toast.success(response.data.message || 'Subtask deleted successfully');
                    // No need to call getTasks() - optimistic update already done
                } else {
                    throw new Error(response.data.message || 'Failed to delete subtask');
                }
            } catch (error) {
                // Rollback - restore subtask at original position
                this.get_selected_task.subtasks.splice(subtaskIndex, 0, originalSubtask);

                // Rollback in main task arrays
                if (this.updateTaskInArrays) {
                    this.updateTaskInArrays(this.get_selected_task.id, {
                        subtasks: [...this.get_selected_task.subtasks]
                    });
                }

                console.error('[TaskNavigation] Error deleting subtask:', error);
                this.$toast.error(error.message || 'Failed to delete subtask');
            }
        },
        scrollToBottom() {
            this.$nextTick(() => {
                var container = this.$el.querySelector("#navigation");
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },
        async createComment() {
            if (!this.new_task_comment?.trim()) {
                this.$toast.error('Please enter a comment');
                return;
            }

            // console.log('[createComment] Starting comment creation');
            // console.log('[createComment] replyingTo:', this.replyingTo);

            const tempComment = {
                id: Date.now(), // Temporary ID
                task_id: this.get_selected_task.id,
                user_id: this.get_user.id,
                comment: this.new_task_comment,
                parent_id: this.replyingTo ? this.replyingTo.id : null,
                created_at: new Date().toISOString(),
                user: this.get_user
            };

            // console.log('[createComment] Created temp comment:', tempComment);

            // Optimistic update - add comment immediately to selected task
            if (!this.get_selected_task.task_comments) {
                this.get_selected_task.task_comments = [];
            }

            // Add to end of array (most recent)
            this.get_selected_task.task_comments.push(tempComment);

            // console.log('[createComment] Comments after adding temp:', this.get_selected_task.task_comments.map(c => ({
            //     id: c.id,
            //     parent_id: c.parent_id,
            //     created_at: c.created_at,
            //     comment: c.comment.substring(0, 20) + '...'
            // })));

            // Also update in the main tasks arrays via updateTaskInArrays
            if (this.updateTaskInArrays) {
                this.updateTaskInArrays(this.get_selected_task.id, {
                    task_comments: [...this.get_selected_task.task_comments]
                });
            }

            // Store original comment for potential rollback
            const originalComment = this.new_task_comment;

            // Extract mentioned user IDs from the comment
            const mentionedUserIds = this.extractMentionedUsers(originalComment);

            // Clear form and reply state
            this.new_task_comment = null;
            this.isCommentInputVisible = false;
            // const wasReplying = this.replyingTo !== null;
            this.replyingTo = null;
            this.highlightedComment = null;
            this.scrollToBottom();

            try {
                const payload = {
                    task_id: this.get_selected_task.id,
                    user_id: this.get_user.id,
                    comment: originalComment,
                    parent_id: tempComment.parent_id,
                    mentioned_user_ids: mentionedUserIds // Send mentioned users to backend
                };

                const response = await this.$axios.post("api/tasks/create_task_comment", payload);

                if (response.data.response) {
                    // console.log('[createComment] Server response successful');
                    // console.log('[createComment] Server response data:', response.data.data);

                    // Replace temp comment with real one from server
                    const commentIndex = this.get_selected_task.task_comments.findIndex(c => c.id === tempComment.id);
                    // console.log('[createComment] Found temp comment at index:', commentIndex);

                    if (commentIndex !== -1) {
                        // Find the newest comment from server response (it should be the one we just created)
                        const allServerComments = response.data.data.task_comments || [];
                        const serverComment = allServerComments.find(c =>
                            c.comment === originalComment &&
                            c.parent_id === tempComment.parent_id &&
                            c.user_id === tempComment.user_id
                        ) || allServerComments[allServerComments.length - 1]; // fallback to last comment

                        console.log('[createComment] Found server comment:', serverComment);

                        if (serverComment) {
                            this.get_selected_task.task_comments[commentIndex] = serverComment;
                            // console.log('[createComment] Replaced temp comment with server comment');
                            // console.log('[createComment] Comments after replacement:', this.get_selected_task.task_comments.map(c => ({
                            //     id: c.id,
                            //     parent_id: c.parent_id,
                            //     created_at: c.created_at,
                            //     comment: c.comment.substring(0, 20) + '...'
                            // })));

                            // Also update in main tasks arrays
                            if (this.updateTaskInArrays) {
                                this.updateTaskInArrays(this.get_selected_task.id, {
                                    task_comments: [...this.get_selected_task.task_comments]
                                });
                            }
                        }
                    }

                    // Success message
                    this.$toast.success('Comment added successfully');
                    this.scrollToBottom();
                } else {
                    // Rollback on server error
                    const commentIndex = this.get_selected_task.task_comments.findIndex(c => c.id === tempComment.id);
                    if (commentIndex !== -1) {
                        this.get_selected_task.task_comments.splice(commentIndex, 1);
                        // Also rollback in main tasks arrays
                        if (this.updateTaskInArrays) {
                            this.updateTaskInArrays(this.get_selected_task.id, {
                                task_comments: [...this.get_selected_task.task_comments]
                            });
                        }
                    }
                    // Restore form
                    this.new_task_comment = originalComment;
                    this.isCommentInputVisible = true;
                    this.$toast.error(response.data.message);
                }
            } catch (error) {
                // Rollback on network error
                const commentIndex = this.get_selected_task.task_comments.findIndex(c => c.id === tempComment.id);
                if (commentIndex !== -1) {
                    this.get_selected_task.task_comments.splice(commentIndex, 1);
                    // Also rollback in main tasks arrays
                    if (this.updateTaskInArrays) {
                        this.updateTaskInArrays(this.get_selected_task.id, {
                            task_comments: [...this.get_selected_task.task_comments]
                        });
                    }
                }
                // Restore form
                this.new_task_comment = originalComment;
                this.isCommentInputVisible = true;
                console.error('Error creating comment:', error);
                this.$toast.error('Failed to create comment');
            }
        },
        toggleUpdateComment(task_comment) {
            this.comment_to_edit.id = task_comment.id;
            this.showNewComment = false;
        },
        async updateComment(task_comment) {
            if (!task_comment.comment?.trim()) {
                this.$toast.error('Please enter a comment');
                return;
            }

            // Store original comment for rollback
            const originalComment = task_comment.comment;

            try {
                const response = await this.$axios.patch(
                    `api/tasks/update_task_comment/${task_comment.id}`,
                    { ...task_comment, comment: task_comment.comment }
                );

                if (response.data.response) {
                    this.$toast.success(response.data.message);
                    this.comment_to_edit.id = null;
                    this.showNewComment = true;
                } else {
                    // Rollback on server error
                    task_comment.comment = originalComment;
                    this.$toast.error(response.data.message);
                }
            } catch (error) {
                // Rollback on network error
                task_comment.comment = originalComment;
                console.error('Error updating comment:', error);
                this.$toast.error('Failed to update comment');
            }
        },
        async deleteComment(task_comment) {
            const commentIndex = this.get_selected_task.task_comments.findIndex(c => c.id === task_comment.id);
            if (commentIndex === -1) {
                this.$toast.error('Comment not found');
                return;
            }

            // Store original comment for rollback
            const originalComment = { ...this.get_selected_task.task_comments[commentIndex] };

            // Optimistic delete - remove immediately
            this.get_selected_task.task_comments.splice(commentIndex, 1);

            // Also update in main tasks arrays
            if (this.updateTaskInArrays) {
                this.updateTaskInArrays(this.get_selected_task.id, {
                    task_comments: [...this.get_selected_task.task_comments]
                });
            }

            try {
                const response = await this.$axios.delete(`api/tasks/delete_task_comment/${task_comment.id}`);

                if (response.data.response) {
                    this.$toast.success(response.data.message);
                    this.getTasks();
                    this.$emit("closeTaskDetailsDialog");
                    this.showNewComment = true;
                } else {
                    // Rollback on server error
                    this.get_selected_task.task_comments.splice(commentIndex, 0, originalComment);
                    // Also rollback in main tasks arrays
                    if (this.updateTaskInArrays) {
                        this.updateTaskInArrays(this.get_selected_task.id, {
                            task_comments: [...this.get_selected_task.task_comments]
                        });
                    }
                    this.$toast.error(response.data.message);
                }
            } catch (error) {
                // Rollback on network error
                this.get_selected_task.task_comments.splice(commentIndex, 0, originalComment);
                // Also rollback in main tasks arrays
                if (this.updateTaskInArrays) {
                    this.updateTaskInArrays(this.get_selected_task.id, {
                        task_comments: [...this.get_selected_task.task_comments]
                    });
                }
                console.error('Error deleting comment:', error);
                this.$toast.error('Failed to delete comment');
            }
        },

        // Reply functionality methods
        startReply(comment) {
            // Use the parent comment's ID if replying to a reply, otherwise use the comment's ID
            const parentId = comment.parent_id || comment.id;
            const parentComment = comment.parent_id
                ? this.get_selected_task.task_comments.find(c => c.id === comment.parent_id)
                : comment;

            this.replyingTo = {
                id: parentId,
                user: parentComment.user,
                comment: parentComment.comment
            };
            this.highlightedComment = parentId;
            this.isCommentInputVisible = true;
            this.showNewComment = true;

            // Focus on the comment input
            this.$nextTick(() => {
                if (this.$refs.editor) {
                    this.$refs.editor.focus();
                }
            });
        },

        cancelReply() {
            this.replyingTo = null;
            this.highlightedComment = null;
            this.isCommentInputVisible = false;
        },

        // Mention autocomplete methods
        handleEditorKeyup(event) {
            const editor = this.$refs.editor?.$el?.querySelector('[contenteditable="true"]');
            if (!editor) return;

            const selection = window.getSelection();
            if (!selection.rangeCount) return;

            const range = selection.getRangeAt(0);
            const textBeforeCursor = range.startContainer.textContent?.substring(0, range.startOffset) || '';
            
            // Check if user just typed @ or is in the middle of typing a mention
            const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
            
            if (mentionMatch && event.key !== 'Escape') {
                this.mentionSearch = mentionMatch[1];
                this.mentionRange = range.cloneRange();
                this.selectedMentionIndex = 0;
                
                // Position dropdown near cursor
                const rect = range.getBoundingClientRect();
                const editorRect = editor.getBoundingClientRect();
                this.mentionDropdownPosition = {
                    top: rect.bottom - editorRect.top + 5,
                    left: rect.left - editorRect.left
                };
                
                this.showMentionDropdown = true;
            } else if (event.key === 'Escape' || !mentionMatch) {
                this.closeMentionDropdown();
            }
        },

        handleEditorKeydown(event) {
            if (!this.showMentionDropdown) return;

            const users = this.filteredMentionUsers;
            
            // Navigate dropdown with arrow keys
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.selectedMentionIndex = Math.min(this.selectedMentionIndex + 1, users.length - 1);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.selectedMentionIndex = Math.max(this.selectedMentionIndex - 1, 0);
            } else if (event.key === 'Enter' && users.length > 0) {
                event.preventDefault();
                this.insertMention(users[this.selectedMentionIndex]);
            } else if (event.key === 'Escape') {
                event.preventDefault();
                this.closeMentionDropdown();
            }
        },

        insertMention(user) {
            if (!this.mentionRange || !user) return;

            const editor = this.$refs.editor?.$el?.querySelector('[contenteditable="true"]');
            if (!editor) return;

            // Find the @ symbol position
            const textNode = this.mentionRange.startContainer;
            const textBeforeCursor = textNode.textContent?.substring(0, this.mentionRange.startOffset) || '';
            const atIndex = textBeforeCursor.lastIndexOf('@');
            
            if (atIndex === -1) {
                this.closeMentionDropdown();
                return;
            }

            // Create mention span element
            const mentionSpan = document.createElement('span');
            mentionSpan.setAttribute('data-user-id', user.id);
            mentionSpan.setAttribute('data-user-name', user.name);
            mentionSpan.className = 'mention-tag';
            mentionSpan.contentEditable = 'false';
            mentionSpan.textContent = `${user.name}`;
            mentionSpan.style.cssText = 'background-color: #e3f2fd; color: #1976d2; padding: 2px 6px; border-radius: 4px; margin: 0 2px; font-weight: 500;';

            // Add a space after the mention
            const spaceNode = document.createTextNode('\u00A0');

            // Create a new range to replace the @ and search text
            const replaceRange = document.createRange();
            replaceRange.setStart(textNode, atIndex);
            replaceRange.setEnd(textNode, this.mentionRange.startOffset);
            
            // Delete the @ and search text
            replaceRange.deleteContents();
            
            // Insert the mention span and space
            replaceRange.insertNode(spaceNode);
            replaceRange.insertNode(mentionSpan);

            // Move cursor after the space
            const newRange = document.createRange();
            newRange.setStartAfter(spaceNode);
            newRange.collapse(true);
            
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(newRange);

            // Update the v-model by triggering input event
            const inputEvent = new Event('input', { bubbles: true });
            editor.dispatchEvent(inputEvent);

            this.closeMentionDropdown();
        },

        selectMentionUser(user) {
            this.insertMention(user);
        },

        closeMentionDropdown() {
            this.showMentionDropdown = false;
            this.mentionSearch = '';
            this.mentionRange = null;
            this.selectedMentionIndex = 0;
        },

        attachMentionListeners() {
            const editor = this.$refs.editor?.$el?.querySelector('[contenteditable="true"]');
            if (editor) {
                editor.addEventListener('keyup', this.handleEditorKeyup);
                editor.addEventListener('keydown', this.handleEditorKeydown);
            }
        },

        removeMentionListeners() {
            const editor = this.$refs.editor?.$el?.querySelector('[contenteditable="true"]');
            if (editor) {
                editor.removeEventListener('keyup', this.handleEditorKeyup);
                editor.removeEventListener('keydown', this.handleEditorKeydown);
            }
        },

        extractMentionedUsers(commentHtml) {
            if (!commentHtml) return [];
            
            // Create a temporary div to parse the HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = commentHtml;
            
            // Find all mention spans
            const mentionSpans = tempDiv.querySelectorAll('.mention-tag');
            const userIds = [];
            
            mentionSpans.forEach(span => {
                const userId = span.getAttribute('data-user-id');
                if (userId && !userIds.includes(parseInt(userId))) {
                    userIds.push(parseInt(userId));
                }
            });
            
            // Filter out the current user (don't notify yourself)
            return userIds.filter(id => id !== this.get_user.id);
        },

        setBorderWidth() {
            let i = this.$refs.drawer.$el.querySelector(
                ".v-navigation-drawer__border"
            );
            i.style.width = this.navigation.borderSize + "px";
            i.style.cursor = "ew-resize";
        },
        setEvents() {
            const minSize = this.navigation.borderSize;
            const el = this.$refs.drawer.$el;
            const drawerBorder = el.querySelector(".v-navigation-drawer__border");
            const vm = this;
            const direction = el.classList.contains("v-navigation-drawer--right")
                ? "right"
                : "left";

            function resize(e) {
                document.body.style.cursor = "ew-resize";
                let f =
                    direction === "right"
                        ? document.body.scrollWidth - e.clientX
                        : e.clientX;
                el.style.width = f + "px";
            }
            drawerBorder.addEventListener(
                "mousedown",
                function (e) {
                    if (e.offsetX < minSize) {
                        el.style.transition = "initial";
                        document.addEventListener("mousemove", resize, false);
                    }
                },
                false
            );
            document.addEventListener(
                "mouseup",
                function () {
                    el.style.transition = "";
                    vm.navigation.width = el.style.width;
                    document.body.style.cursor = "";
                    document.removeEventListener("mousemove", resize, false);
                },
                false
            );
        },
        getColor(name) {
            const letterColorMap = {
                A: "red lighten-2",
                B: "blue lighten-2",
                C: "green lighten-2",
                D: "orange lighten-2",
                E: "purple lighten-2",
                F: "pink lighten-2",
                G: "teal lighten-2",
                H: "amber lighten-2",
                I: "indigo lighten-2",
                J: "lime lighten-2",
                K: "brown lighten-2",
                L: "cyan lighten-2",
                M: "deep-orange lighten-2",
                N: "deep-purple lighten-2",
                O: "light-blue lighten-2",
                P: "light-green lighten-2",
                Q: "blue-grey lighten-2",
                R: "red darken-2",
                S: "blue darken-2",
                T: "green darken-2",
                U: "orange darken-2",
                V: "purple darken-2",
                W: "pink darken-2",
                X: "teal darken-2",
                Y: "amber darken-2",
                Z: "indigo darken-2",
            };
            const firstLetter = name.charAt(0).toUpperCase();
            return letterColorMap[firstLetter] || "grey lighten-2";
        },

        getAvatarColor(name) {
            if (!name) return '#9E9E9E';

            // Use first 3 letters for consistency
            const key = name.substring(0, 3).toUpperCase();

            // Generate a hash from the first 3 letters
            let hash = 0;
            for (let i = 0; i < key.length; i++) {
                hash = key.charCodeAt(i) + ((hash << 5) - hash);
            }

            // Array of pleasing colors
            const colors = [
                '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
                '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
                '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2',
                '#A9DFBF', '#F9E79F', '#D5A6BD', '#AED6F1', '#A3E4D7',
                '#FCF3CF', '#FADBD8', '#D6EAF8', '#E8DAEF', '#D1F2EB'
            ];

            // Use hash to select color consistently
            const colorIndex = Math.abs(hash) % colors.length;
            return colors[colorIndex];
        },

        getInitials(name) {
            return name
                ? name.split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                : "?";
        },

        // Activity timeline formatting methods
        getActivityIcon(action) {
            const iconMap = {
                'created': 'mdi-plus-circle',
                'updated': 'mdi-pencil',
                'assigned': 'mdi-account-plus',
                'unassigned': 'mdi-account-minus',
                'deleted': 'mdi-delete',
                'completed': 'mdi-check-circle'
            };
            return iconMap[action] || 'mdi-information';
        },

        getActivityIconColor(action) {
            const colorMap = {
                'created': 'success',
                'updated': 'info',
                'assigned': 'primary',
                'unassigned': 'warning',
                'deleted': 'error',
                'completed': 'success'
            };
            return colorMap[action] || 'grey';
        },

        formatActivityDescription(activity) {
            const action = activity.action;
            const fieldName = activity.field_name;
            const oldValue = activity.old_value;
            const newValue = activity.new_value;

            // Use custom description if available
            if (activity.description) {
                return activity.description;
            }

            // Format based on action type
            switch (action) {
                case 'created':
                    return 'created this task';
                case 'deleted':
                    return 'deleted this task';
                case 'assigned':
                    return `assigned user ID ${newValue} to this task`;
                case 'unassigned':
                    return `unassigned user ID ${oldValue} from this task`;
                case 'updated':
                    if (fieldName && oldValue !== null && newValue !== null) {
                        return this.formatFieldChange(fieldName, oldValue, newValue);
                    }
                    return 'updated this task';
                default:
                    return `performed ${action} action`;
            }
        },

        formatFieldChange(fieldName, oldValue, newValue) {
            const fieldDisplayNames = {
                'name': 'Task name',
                'description': 'Description',
                'status_id': 'Status',
                'priority_id': 'Priority',
                'due_date': 'Due date',
                'company_id': 'Company',
                'project_id': 'Project',
                'meeting_id': 'Meeting',
                'start_time': 'Start time',
                'end_time': 'End time',
                'allocated_time': 'Allocated time'
            };

            const fieldDisplay = fieldDisplayNames[fieldName] || fieldName;

            // Handle specific field formatting
            switch (fieldName) {
                case 'status_id':
                    return `changed status from "${this.getStatusName(oldValue)}" to "${this.getStatusName(newValue)}"`;
                case 'priority_id':
                    return `changed priority from "${this.getPriorityName(oldValue)}" to "${this.getPriorityName(newValue)}"`;
                case 'due_date': {
                    const oldDate = oldValue ? this.$date(oldValue).format('MMM DD') : 'none';
                    const newDate = newValue ? this.$date(newValue).format('MMM DD') : 'none';
                    return `changed due date from ${oldDate} to ${newDate}`;
                }
                case 'start_time':
                case 'end_time': {
                    const oldTime = oldValue ? this.$date(oldValue).format(`MMM DD, ${this.$tz.time}`) : 'none';
                    const newTime = newValue ? this.$date(newValue).format(`MMM DD, ${this.$tz.time}`) : 'none';
                    return `changed ${fieldDisplay.toLowerCase()} from ${oldTime} to ${newTime}`;
                }
                default:
                    return `changed ${fieldDisplay.toLowerCase()} from "${oldValue}" to "${newValue}"`;
            }
        },

        getStatusName(statusId) {
            if (!statusId) return 'Unknown';
            const status = this.get_statuses.find(s => s.id == statusId);
            return status ? status.name : `Status ${statusId}`;
        },

        getPriorityName(priorityId) {
            if (!priorityId) return 'Unknown';
            const priority = this.get_priorities.find(p => p.id == priorityId);
            return priority ? priority.name : `Priority ${priorityId}`;
        },

        getPriorityChipColor(priorityId) {
            const colorMap = {
                1: '#9C27B0', // Urgent - Purple
                2: '#F44336', // High - Red
                3: '#FF9800', // Medium - Orange
                4: '#4CAF50', // Low - Green
                null: '#9E9E9E'    // None - Grey
            };
            return colorMap[priorityId] || '#9E9E9E';
        },

        getPriorityTextColor(priorityId) {
            return priorityId ? 'white' : 'rgba(0,0,0,0.6)';
        },
        getStatusChipColor(statusId) {
            const colorMap = {
                1: '#9E9E9E',      // To Do - Grey
                2: '#2196F3',      // In Progress - Blue
                3: '#4CAF50',      // In Review - Green
                4: '#4CAF50',      // Completed - Green
                5: '#FF9800',      // Blocked - Orange
                6: '#795548',      // Cancelled - Brown
                7: '#2196F3',      // Recurring - Blue
                null: '#9E9E9E'    // None - Grey
            };
            return colorMap[statusId] || '#9E9E9E';
        },

        getStatusTextColor(statusId) {
            const lightStatuses = [null];
            return lightStatuses.includes(statusId) ? 'rgba(0,0,0,0.6)' : 'white';
        },

        getDueDateChipColor(date) {
            if (!date) return '#E0E0E0';
            const today = new Date();
            const dueDate = new Date(date);
            const diffTime = dueDate - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays < 0) return '#F44336'; // Overdue - Red
            if (diffDays === 0) return '#FF9800'; // Due today - Orange
            if (diffDays <= 3) return '#FFC107'; // Due soon - Amber
            return '#4CAF50'; // Due normal - Green
        },

        getDueDateTextColor(date) {
            return date ? 'white' : 'rgba(0,0,0,0.6)';
        },

        formatDueDate(date) {
            if (!date) return "No due date";
            return new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        },
        async addDescription() {
            try {
                const tp = {
                    record_id: this.get_selected_task.id,
                    description: this.get_selected_task.description,
                };
                await this.addDescriptionAPI(tp);

                // Refresh tasks using injected method
                if (typeof this.refreshTasks === 'function') {
                    await this.refreshTasks();
                }
                this.$toast.success('Description updated successfully');
            } catch (error) {
                console.error('Error adding description:', error);
                this.$toast.error('Failed to update description');
            }
        },

        /**
         * add description to a task
         * @params
         * data?: object
         * - record_id?: int
         * - description>L string
         */
        async addDescriptionAPI(data) {
            try {
                await this.$axios.post('/api/misc/add_description', data)
                return true;
            } catch (error) {
                // console.log("error on adding description", error)
            }
        },

        // Attachment handling methods
        async handleResourcesAttached(resources) {
            if (!this.get_selected_task || !this.get_selected_task.id) {
                this.$toast.error('No task selected');
                return;
            }

            try {
                // Prepare attachments array for API - convert from new resource format to backend format
                const newAttachments = resources.map(resource => ({
                    resource_id: resource.id,
                    file_name: resource.name,
                    file_url: resource.url,
                    file_type: resource.file_type,
                    file_size: resource.size,
                    mime_type: resource.mime_type || this.getMimeTypeFromFileType(resource.file_type),
                    category: resource.category,
                    created_by: this.get_user.id
                }));

                // Combine existing attachments with new ones for API call
                const allAttachments = [...(this.get_selected_task.attachments || []), ...newAttachments];

                // Update task with new attachments
                const response = await this.$axios.post(`api/tasks/update_task/${this.get_selected_task.id}`, {
                    attachments: allAttachments
                });

                if (response.data.response) {
                    // Optimistically update the task with new attachments
                    if (!this.get_selected_task.attachments) {
                        this.get_selected_task.attachments = [];
                    }

                    this.get_selected_task.attachments.push(...newAttachments);

                    // Update in main task arrays
                    if (this.updateTaskInArrays) {
                        this.updateTaskInArrays(this.get_selected_task.id, {
                            attachments: [...this.get_selected_task.attachments]
                        });
                    }

                    this.$toast.success(`${resources.length} attachment${resources.length > 1 ? 's' : ''} added successfully`);
                } else {
                    this.$toast.error('Failed to attach resources');
                }
            } catch (error) {
                console.error('Error attaching resources:', error);
                this.$toast.error('Failed to attach resources');
            }
        },

        viewAttachment(attachment) {
            try {
                // For URL resources, open the URL directly
                if (this.isUrlResource(attachment) || (attachment.file_url && (attachment.file_url.startsWith('http://') || attachment.file_url.startsWith('https://')))) {
                    window.open(attachment.file_url, '_blank');
                    this.$toast.success(`Opened ${attachment.file_name}`);
                } else if (attachment.file_url) {
                    // For file resources, open via API URL
                    const viewUrl = this.getViewUrl(attachment.file_url);
                    if (viewUrl && viewUrl !== '#') {
                        window.open(viewUrl, '_blank');
                        this.$toast.success(`Opened ${attachment.file_name}`);
                    } else {
                        this.$toast.warning('No valid URL for this attachment');
                    }
                } else {
                    this.$toast.error('No file or URL available for this attachment');
                }
            } catch (error) {
                console.error('Error viewing attachment:', error);
                this.$toast.error('Failed to open attachment');
            }
        },

        async downloadAttachment(attachment) {
            try {
                // For URL resources, open the URL directly
                if (this.isUrlResource(attachment) || (attachment.file_url && (attachment.file_url.startsWith('http://') || attachment.file_url.startsWith('https://')))) {
                    window.open(attachment.file_url, '_blank');
                    this.$toast.success(`Opened ${attachment.file_name}`);
                } else if (attachment.file_url) {
                    // For file resources, download via API
                    const apiBaseUrl = process.env.VUE_APP_API_URL || '';
                    const downloadUrl = `${apiBaseUrl}/api/resources/${attachment.resource_id}/download`;
                    
                    const response = await this.$axios.get(downloadUrl, {
                        responseType: 'blob'
                    });

                    // Create download link
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', attachment.file_name);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    this.$toast.success(`Downloaded ${attachment.file_name}`);
                } else {
                    this.$toast.error('No file or URL available for this attachment');
                }
            } catch (error) {
                console.error('Error accessing attachment:', error);
                this.$toast.error('Failed to access attachment');
            }
        },

        async removeAttachment(attachmentId) {
            if (!this.get_selected_task || !this.get_selected_task.id) {
                this.$toast.error('No task selected');
                return;
            }

            // Find attachment by resource_id or by index if resource_id doesn't exist
            let attachmentIndex = -1;
            if (typeof attachmentId === 'number' && this.get_selected_task.attachments[attachmentId]) {
                // If attachmentId is an index
                attachmentIndex = attachmentId;
            } else {
                // Find by resource_id
                attachmentIndex = this.get_selected_task.attachments?.findIndex(a => a.resource_id === attachmentId);
            }

            if (attachmentIndex === -1 || attachmentIndex === undefined) {
                this.$toast.error('Attachment not found');
                return;
            }

            // Store original attachment for rollback
            const originalAttachment = { ...this.get_selected_task.attachments[attachmentIndex] };

            // Optimistic delete - remove from UI immediately
            this.get_selected_task.attachments.splice(attachmentIndex, 1);

            // Also update in main task arrays
            if (this.updateTaskInArrays) {
                this.updateTaskInArrays(this.get_selected_task.id, {
                    attachments: [...this.get_selected_task.attachments]
                });
            }

            try {
                // Send the remaining attachments array to the API
                const response = await this.$axios.post(`api/tasks/update_task/${this.get_selected_task.id}`, {
                    attachments: this.get_selected_task.attachments
                });

                if (response.data.response) {
                    this.$toast.success('Attachment removed successfully');
                } else {
                    // Rollback on API failure
                    this.get_selected_task.attachments.splice(attachmentIndex, 0, originalAttachment);
                    if (this.updateTaskInArrays) {
                        this.updateTaskInArrays(this.get_selected_task.id, {
                            attachments: [...this.get_selected_task.attachments]
                        });
                    }
                    this.$toast.error('Failed to remove attachment');
                }
            } catch (error) {
                // Rollback on network error
                this.get_selected_task.attachments.splice(attachmentIndex, 0, originalAttachment);
                if (this.updateTaskInArrays) {
                    this.updateTaskInArrays(this.get_selected_task.id, {
                        attachments: [...this.get_selected_task.attachments]
                    });
                }
                console.error('Error removing attachment:', error);
                this.$toast.error('Failed to remove attachment');
            }
        },

        // Resource helper methods (same as ResourceCard)
        getFileTypeIcon(fileType) {
            const iconMap = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word-box',
                docx: 'mdi-file-word-box',
                xls: 'mdi-file-excel-box',
                xlsx: 'mdi-file-excel-box',
                ppt: 'mdi-file-powerpoint-box',
                pptx: 'mdi-file-powerpoint-box',
                txt: 'mdi-file-document-outline',
                zip: 'mdi-folder-zip',
                image: 'mdi-file-image-box',
                video: 'mdi-file-video-box',
                audio: 'mdi-file-music-box',
                // URL-based resource icons
                'google-sheets': 'mdi-google-spreadsheet',
                'google-docs': 'mdi-file-document',
                'google-slides': 'mdi-google-slides',
                'google-drive': 'mdi-google-drive',
                'office-365': 'mdi-microsoft-office',
                'url': 'mdi-link'
            };
            return iconMap[fileType] || 'mdi-file-document-outline';
        },

        getFileTypeColor(fileType) {
            const colorMap = {
                pdf: 'red',
                doc: 'blue',
                docx: 'blue',
                xls: 'green',
                xlsx: 'green',
                ppt: 'orange',
                pptx: 'orange',
                txt: 'grey',
                zip: 'purple',
                image: 'pink',
                video: 'deep-purple',
                audio: 'teal',
                // URL-based resource colors
                'google-sheets': 'green',
                'google-docs': 'blue',
                'google-slides': 'orange',
                'google-drive': 'blue',
                'office-365': 'red',
                'url': 'indigo'
            };
            return colorMap[fileType] || 'grey';
        },

        getViewUrl(url) {
            // If URL already has a protocol, use it as-is
            if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                return url;
            }
            // Otherwise, prepend the API base URL
            const apiBaseUrl = process.env.VUE_APP_API_URL || '';
            return url ? `${apiBaseUrl}${url}` : '#';
        },

        formatFileSize(bytes) {
            if (!bytes) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        },

        getMimeTypeFromFileType(fileType) {
            const mimeMap = {
                pdf: 'application/pdf',
                doc: 'application/msword',
                docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                xls: 'application/vnd.ms-excel',
                xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ppt: 'application/vnd.ms-powerpoint',
                pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                txt: 'text/plain',
                zip: 'application/zip',
                image: 'image/jpeg',
                video: 'video/mp4',
                audio: 'audio/mpeg'
            };
            return mimeMap[fileType] || 'application/octet-stream';
        }

    }
};
</script>

<style lang="scss" scoped>
.task-details-drawer {
    z-index: 9999 !important;
    
    :deep(.v-navigation-drawer__content) {
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }
}

.task-details-container {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: white;
}

// Header Section
.task-header {
    flex-shrink: 0;
    padding: 14px 20px 12px;
    border-bottom: 1px solid #e0e0e0;
    background: white;
}

.task-header-top {
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.task-header-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding-top: 22px; // aligns with the title field, below its label
}

// Matches MyWorkComponent's .icon-action-btn — borderless icon buttons
.icon-action-btn {
    box-shadow: none !important;
}

.task-title-section {
    flex: 1;
    min-width: 0;
}

.task-title-field {
    border: 1px solid #e0e0e0;
    border-radius: 20px;

    :deep(.v-input__control) {
        min-height: 40px;
    }

    :deep(.v-input__slot) {
        border-radius: 20px !important;
    }

    :deep(.v-text-field__details) {
        display: none;
    }

    :deep(textarea) {
        font-size: 0.95rem;
        line-height: 1.4;
    }
}

.task-context {
    padding: 12px;
    background: #f5f5f5;
    border-radius: 8px;
    border-left: 4px solid #2196f3;
}

.context-label {
    font-size: 0.875rem;
    color: #666;
    margin-bottom: 4px;
}

.context-content {
    font-weight: 500;
    color: #333;
}

// Main Content
.task-content {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

// Properties Grid - Compact Layout
.task-properties {
    padding: 16px 20px;
}

.property-grid-compact {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.property-row {
    display: flex;
    gap: 16px;
    align-items: flex-start;

    + .property-row {
        margin-top: 16px;
    }
}

.property-row-quad {
    .field-block {
        flex: 1;
        min-width: 0;
    }
}

.field-block {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.field-label {
    font-weight: 500;
    font-size: 0.78rem;
    color: #555;
}

.field-label-required::after {
    content: ' *';
    color: #e53935;
}

// Matches MyWorkComponent's .filter-search-style — rounded, soft-bordered
// solo/flat pill fields, applied uniformly across every field in this drawer
// (Company/Assigned To, Priority/Status/Due Date/Tags, Time Allocation, Recurrence).
.field-outlined {
    border: 1px solid #e0e0e0;
    border-radius: 20px;

    :deep(.v-input__control) {
        min-height: 40px;
    }

    :deep(.v-input__slot) {
        border-radius: 20px !important;
    }

    :deep(.v-select__selection),
    :deep(input) {
        font-size: 0.85rem;
    }
}

.field-inline-value {
    display: flex;
    align-items: center;
    font-size: 0.85rem;
    color: #333;
}

// Non-Vuetify-input fields styled to match .field-outlined: the Due Date /
// Series Start / Series End menu triggers (need a click target that isn't a
// text field) and the read-only Allocated display.
.field-button,
.field-static {
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 0 12px;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    background: white;
}

.field-button {
    cursor: pointer;

    &:hover {
        border-color: rgba(0, 0, 0, 0.4);
    }
}

.field-static {
    color: #333;
}

// Additional Details Section
.details-section {
    .details-header {
        padding: 16px 20px;
        min-height: 48px !important;

        .details-title {
            display: flex;
            align-items: center;
            font-weight: 600;
            color: #666;
        }
    }

    :deep(.v-expansion-panel-content__wrap) {
        padding: 0 20px 16px;
    }
}

.additional-details {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 12px;
}

.detail-label {
    font-weight: 600;
    font-size: 0.8rem;
    color: #666;
    min-width: 80px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.detail-value {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
}

.week-info {
    font-size: 0.75rem;
    color: #666;
    margin-left: 8px;
}

.meeting-link {
    color: #2196f3;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}

.no-value {
    color: #999;
    font-style: italic;
}

.creator-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.creator-avatar {
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.avatar-text {
    font-size: 10px;
    font-weight: 600;
    color: white;
}

.chip-avatar-text {
    font-size: 8px;
    font-weight: 600;
}

.assignee-select {
    min-width: 200px;
}

.assignee-chip {
    margin: 2px;
    font-size: 0.75rem;

    :deep(.v-avatar) {
        margin-left: -8px;
        margin-right: 4px;
    }
}

// Chip Fields Styling
.chip-field {
    :deep(.v-input__control) {
        min-height: 32px;
    }

    :deep(.v-input__slot) {

        &:before,
        &:after {
            display: none !important;
        }

        background: transparent !important;
        box-shadow: none !important;
    }

    :deep(.v-select__selection) {
        margin: 0;
        max-width: 100%;
    }
}

.due-date-chip,
.priority-chip,
.status-chip,
.tag-chip {
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
}

.datetime-field {
    max-width: 200px;

    :deep(.v-input__control) {
        min-height: 32px;
    }

    :deep(.v-text-field__details) {
        display: none;
    }

    :deep(input[type="datetime-local"]) {
        font-size: 0.875rem;
    }
}

.time-chip {
    :deep(.vue__time-picker) {
        border: none;
        background: transparent;
    }
}

// Section Styling - More Compact
.section-divider {
    margin: 0 20px;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 12px;
}

.section-title {
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin: 0;

    .v-icon {
        margin-right: 8px;
        color: #666;
    }
}

// Time Allocation / Recurrence section headers sit inside .task-properties
// (which already has its own padding), so they don't need the horizontal
// padding .section-header normally adds.
.section-header-tight {
    padding: 0 0 12px;
}

.recurrence-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.recurrence-row-days {
    justify-content: space-between;
    margin-bottom: 8px;
}

.recurrence-label {
    font-size: 0.85rem;
    color: #555;
    white-space: nowrap;
}

.recurrence-interval-field {
    max-width: 70px;
}

.recurrence-unit-field {
    min-height: 40px;
    font-size: 0.85rem;
}

.recurrence-toggle-group {
    box-shadow: none;
}

.recurrence-days {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;
}

.recurrence-banner {
    display: flex;
    align-items: center;
    margin-top: 16px;
    padding: 10px 14px;
    border-radius: 8px;
    background: #e8f5e9;
    color: #2e7d32;
    font-size: 0.85rem;
}

.subtask-count,
.comment-count {
    margin-left: 8px;
    font-size: 0.75rem;
    background: #e3f2fd;
    color: #1976d2;
}

// Description Section - More Compact
.description-section {
    .description-content {
        padding: 0 20px 16px;
    }

    .save-description-btn {
        margin-top: 8px;
    }
}

// Subtasks Section - More Compact
.subtasks-section {
    .subtasks-content {
        padding: 0 20px 16px;
    }

    .add-subtask-btn {
        margin-left: auto;
    }
}

// Attachments Section
.attachments-section {
    .attachments-content {
        padding: 0 20px 16px;
    }

    .add-attachment-btn {
        margin-left: auto;
    }
}

.attachments-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.attachment-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    transition: all 0.2s ease;

    &:hover {
        background: #f0f2f5;
        border-color: #ccc;
    }
}

.attachment-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.attachment-icon {
    font-size: 24px;
    flex-shrink: 0;
}

.attachment-details {
    flex: 1;
    min-width: 0;
}

.attachment-name {
    font-weight: 500;
    font-size: 0.9rem;
    color: #333;
    margin-bottom: 2px;
    word-break: break-word;
}

.attachment-meta {
    display: flex;
    gap: 12px;
    font-size: 0.75rem;
    color: #666;
}

.attachment-size {
    background: #e3f2fd;
    color: #1976d2;
    padding: 2px 6px;
    border-radius: 4px;
}

.attachment-category {
    background: #f3e5f5;
    color: #7b1fa2;
    padding: 2px 6px;
    border-radius: 4px;
}

.attachment-actions {
    display: flex;
    gap: 4px;
    align-items: center;
}

.attachment-count {
    margin-left: 8px;
    font-size: 0.75rem;
    background: #e3f2fd;
    color: #1976d2;
}

.no-attachments {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    color: #999;

    .v-icon {
        font-size: 48px;
        margin-bottom: 8px;
    }
}

.subtasks-table {
    :deep(.v-data-table__wrapper) {
        border-radius: 8px;
        border: 1px solid #e0e0e0;
    }

    :deep(thead tr th) {
        background: #f5f5f5;
        font-weight: 600;
        border-bottom: 2px solid #e0e0e0;
    }
}

.subtask-row {
    &:hover {
        background: #f8f9fa;
    }
}

.subtask-name-field,
.subtask-select {
    :deep(.v-input__slot) {

        &:before,
        &:after {
            display: none !important;
        }
    }
}

.quick-subtask-row {
    td {
        padding: 16px !important;
    }
}

.quick-subtask-field {
    :deep(.v-input__control) {
        min-height: 40px;
    }
}

.no-subtasks,
.no-comments {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    color: #999;

    .v-icon {
        font-size: 48px;
        margin-bottom: 8px;
    }
}

// Comments Section
.comments-section {
    .comments-content {
        padding: 0 20px 20px;
    }
}

.comments-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.comment-item {
    padding: 16px;
    background: #f9f9f9;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
}

.comment-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
}

.comment-avatar {
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.comment-meta {
    margin-left: 12px;
}

.comment-author {
    font-weight: 600;
    font-size: 0.875rem;
    color: #333;
}

.comment-time {
    font-size: 0.75rem;
    color: #666;
}

.comment-content {
    padding-left: 44px;
    color: #333;
    line-height: 1.5;

    :deep(p) {
        margin-bottom: 8px;

        &:last-child {
            margin-bottom: 0;
        }
    }
}

.comment-edit {
    padding-left: 44px;
}

.comment-edit-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    justify-content: flex-end;
}

// Comment Footer
.comment-footer {
    flex-shrink: 0;
    padding: 20px;
    border-top: 1px solid #e0e0e0;
    background: white;
}

.add-comment-prompt {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 6px 6px 8px;
    border: 1px solid #e0e0e0;
    border-radius: 24px;
    cursor: text;
    transition: border-color 0.2s;

    &:hover {
        border-color: rgba(0, 0, 0, 0.4);
    }
}

.add-comment-placeholder {
    flex: 1;
    color: #888;
    font-size: 0.9rem;
}

.add-comment-post-btn {
    box-shadow: none !important;
}

.comment-input-section {
    position: relative; /* For mention dropdown positioning */
    
    .comment-input-header {
        margin-bottom: 12px;
    }

    .comment-submit-actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 12px;
    }
}

// User Dropdown
.user-dropdown {
    position: absolute;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-height: 200px;
    overflow-y: auto;
    width: 100%;
    z-index: 1000;

    ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    li {
        padding: 12px 16px;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
            background: #f5f5f5;
        }
    }
}

// Hide default borders and styling
:deep(.hide-border > .v-input__control > .v-input__slot:before,
    .hide-border > .v-input__control > .v-input__slot:after) {
    border: none !important;
}

// Scrollbar styling
:deep(::-webkit-scrollbar) {
    width: 8px;
}

:deep(::-webkit-scrollbar-track) {
    background: #f1f1f1;
    border-radius: 4px;
}

:deep(::-webkit-scrollbar-thumb) {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
        background: #a8a8a8;
    }
}

// Comments content styling
:deep(.comment) {
    display: contents !important;
    white-space: initial;
}

// Responsive adjustments
@media (max-width: 768px) {
    .task-header {
        padding: 12px 16px;
    }

    .property-row,
    .property-row-quad {
        flex-direction: column;
        gap: 12px;
    }

    .field-block {
        flex: 1;
    }

    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .comment-content,
    .comment-edit {
        padding-left: 0;
    }
}

// Activity Timeline Styles
.activity-section {
    .v-tabs {
        border-radius: 8px 8px 0 0;
        overflow: hidden;
    }

    .v-tab {
        text-transform: none;
        font-weight: 500;

        &--active {
            background-color: rgba(25, 118, 210, 0.1);
        }
    }

    .tab-content {
        padding: 0;
        min-height: 200px;
        max-height: 600px;
        overflow-y: scroll;
    }
}

.activity-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
}

.activity-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    background: white;
    transition: all 0.2s ease;

    &.is-comment {
        background: #f8f9fa;
        border-left: 4px solid #2196f3;
    }

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        border-color: #ccc;
    }
}

.activity-content {
    flex: 1;
    min-width: 0;
}

.activity-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.activity-avatar {
    flex-shrink: 0;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.activity-meta {
    flex: 1;
    min-width: 0;
}

.activity-summary {
    font-size: 0.9rem;
    line-height: 1.4;
    color: #333;
    margin-bottom: 4px;
    word-wrap: break-word;

    strong {
        font-weight: 600;
        color: #1976d2;
    }
}

.activity-time {
    font-size: 0.75rem;
    color: #666;
}

.activity-icon {
    flex-shrink: 0;
    margin-top: 4px;
}

.comment-text {
    margin-top: 8px;
    padding-left: 44px;
    color: #333;
    line-height: 1.5;

    :deep(p) {
        margin: 0 0 8px 0;

        &:last-child {
            margin-bottom: 0;
        }
    }
}

.no-activity {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    color: #999;
    text-align: center;

    .v-icon {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.5;
    }

    span {
        font-size: 1.1rem;
        font-weight: 500;
    }
}

// Enhanced section header for activity
.activity-section .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 20px 0;
    border-bottom: none;

    .section-title {
        margin: 0;
    }
}

// Reply functionality styles
.reply-comment {
    margin-left: 32px;
    border-left: 3px solid #e3f2fd;
    padding-left: 16px;
    background: #fafbfc;

    &.highlighted-comment {
        background: #fff3e0;
        border-left-color: #ff9800;
    }
}

.highlighted-comment {
    background: #fff3e0 !important;
    border-color: #ff9800 !important;
    box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.2);
}

.comment-actions {
    margin-top: 8px;
    padding-left: 44px;

    .reply-btn {
        font-size: 0.75rem;
        min-height: 28px;
        padding: 0 12px;
    }
}

.reply-indicator {
    background: #e3f2fd;
    border: 1px solid #bbdefb;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 16px;

    .reply-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;

        .reply-text {
            font-size: 0.875rem;
            color: #1976d2;
            flex: 1;
        }

        .cancel-reply-btn {
            margin-left: auto;
        }
    }

    .reply-preview {
        font-size: 0.8rem;
        color: #666;
        background: white;
        padding: 8px 12px;
        border-radius: 4px;
        border-left: 3px solid #2196f3;
        max-height: 60px;
        overflow: hidden;
        position: relative;

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            right: 0;
            width: 100%;
            height: 20px;
            background: linear-gradient(transparent, white);
        }
    }
}

// Adjust activity items for replies
.activity-item.is-comment.reply-comment {
    margin-left: 48px;
    border-left: 4px solid #e3f2fd;

    &.highlighted-comment {
        border-left-color: #ff9800;
    }
}

/* Mention Autocomplete Dropdown */
.mention-dropdown {
    position: absolute;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-height: 200px;
    overflow-y: auto;
    min-width: 250px;
}

.mention-dropdown-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover,
    &.mention-dropdown-item-selected {
        background-color: #f5f5f5;
    }

    &.no-results {
        color: #999;
        font-size: 0.875rem;
        justify-content: center;
        cursor: default;

        &:hover {
            background-color: transparent;
        }
    }
}

.mention-user-info {
    flex: 1;
    min-width: 0;
}

.mention-user-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mention-user-email {
    font-size: 0.75rem;
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Style mentions in the editor content */
.mention-tag {
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 2px 6px;
    border-radius: 4px;
    margin: 0 2px;
    font-weight: 500;
    cursor: default;
    user-select: none;
}
</style>
